import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PageLayout } from "../components/PageLayout.js";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { InspectorFlyout } from "../components/inspector/InspectorFlyout.js";
import { useToast } from "../components/Toast.js";
import { useConfirm } from "../components/ConfirmDialog.js";
import { ScopeHeader } from "../components/ScopeHeader.js";
import { useMyGroups } from "../lib/scope.js";
import { Chart } from "../components/Chart.js";
import { KpiCard } from "../components/KpiCard.js";
import { TanTableViz } from "../components/TanTableViz.js";
import { AiContentRenderer } from "../components/AiContentRenderer.js";
import { AiProductionPromptPanel } from "../components/AiProductionPromptPanel.js";
import { PageActiveFilterChips } from "../components/PageActiveFilterChips.js";
import { PageFilterDropdown } from "../components/PageFilterDropdown.js";
import { PageFilterDateRange } from "../components/PageFilterDateRange.js";
import { PageSourcePickerModal, type PageBindableSourceRow } from "../components/PageSourcePickerModal.js";
import { WriteConflictBanner } from "../components/WriteConflictBanner.js";
import { readWriteConflict, type WriteConflict } from "../lib/writeConflict.js";
import { createSingleFlight } from "../lib/singleFlight.js";
import {
  applyTanTableRowSelectToSelections,
  distinctValues,
  effectiveFilterSelectionsForRender,
  filterRenderModel,
  filterSourceKey,
  isDateRangeSlicer,
  isPageSlicerFilter,
  resolveEchartsOption,
  type PageFilter,
  type PageFilterRenderModel as RenderModel,
  type TanTableRowSelectEvent
} from "../lib/pageFilters.js";
import {
  collectWidgetIdsInPanelTree,
  createDefaultTabsWidget,
  findContentWidgetById,
  flattenContentWidgets,
  isTabsWidget,
  removeTabPanelFromTabsWidget,
  removeWidgetFromPage,
  indexBatchResolve,
  isPreparingItem,
  PREPARING_POLL_MAX,
  PREPARING_POLL_MS,
  resolvedSourcePort,
  sourcesFromWidgetsUnique,
  updateContentWidgetById,
  updateTabsWidgetById,
  appendContentWidgetToTab,
  appendWidgetToTab,
  findSiblingOccupantsForWidget,
  findTabParentForContent,
  findTabsWidgetById,
  findWidgetGridById,
  updateWidgetGridById,
  type PageContentWidget,
  type PageGrid,
  type PageTabsWidget,
  type PageWidget,
  type PageWidgetSource,
  vizNodeLabelKey,
  widgetSourceKey
} from "../lib/pageWidgets.js";
import { refreshPageData, refreshToastMessages } from "../lib/pageRefresh.js";
import {
  dynamicChartFiltersToColumnFilters,
  hasActiveFilterSelections,
  hasActiveDateRangeSelections,
  nextApplyChartDynamicFilter,
  nextClearAllFilterSelections,
  nextClearAllDateRangeSelections,
  nextClearFilterSelection,
  nextClearDateRange,
  nextRemoveDynamicChartFilter,
  nextSelectAllFilterValues,
  nextSetDateRange,
  nextToggleDynamicChartFilter,
  nextToggleFilterValue,
  type DateRangeSelections
} from "../lib/pageFilterInteractions.js";

type PageStatus = "draft" | "published" | "archived";
type PageSpecV1 = {
  version: "page.v1";
  layout: {
    cols: 12;
    rowHeight: number;
    gap: number;
    marginX?: number;
    marginY?: number;
    background?: string;
  };
  filters?: PageFilter[];
  widgets: PageWidget[];
};
type PageRecord = {
  id: string;
  title: string;
  route_key: string;
  description: string | null;
  status: PageStatus;
  page_spec: PageSpecV1;
  is_viewer_landing: boolean;
  /** ADR-0159: the version token every spec save must present. */
  updated_at?: string;
  last_write_actor_label?: string;
  last_write_source_display?: string;
  // ADR-0068 scope fields — returned by GET /tenant/pages/:id (ScopedPage).
  scope?: import("../lib/scope.js").ContentScope;
  owner_account_id?: string | null;
  owner_group_id?: string | null;
  owner_group_name?: string | null;
};
type BatchResolveItem = {
  source_key: string;
  name?: string;
  source?: PageWidgetSource;
  upstream_ai_provider?: boolean;
  citation_sources?: Array<{ ref_id: string; url: string; title: string }>;
  last?: { renderModel?: RenderModel; runAt?: string | null };
  meta?: {
    asOfRunAt: string | null;
    rowCount?: number | null;
    durationMs?: number | null;
    needsRecompute?: boolean;
    lastError?: string | null;
  };
};

type AddAnchor = {
  x: number;
  y: number;
  left: number;
  top: number;
  /** When set, add actions target this tab panel (nested containers allowed). */
  tabTarget?: { containerId: string; tabId: string };
};
type DragMode = "move" | "resize-e" | "resize-s" | "resize-se" | "resize-w" | "resize-n" | "resize-nw";
/** Where the dragged widget lived at drag start, or where it will land. */
type SurfaceLocation =
  | { kind: "root" }
  | { kind: "tab"; containerId: string; tabId: string };
type DragState = {
  widgetId: string;
  mode: DragMode;
  startClientX: number;
  startClientY: number;
  startScrollY: number;
  origin: PageGrid;
  /** Siblings on the same 12-col surface (root or tab panel) for overlap checks. */
  occupants: GridOccupant[];
  /** Surface the widget lived on when drag began. */
  sourceSurface: SurfaceLocation;
  /** Pointer offset inside the widget at drag start, expressed in grid columns/rows on the source surface. */
  grabOffsetCol: number;
  grabOffsetRow: number;
  /** Ids in the dragged subtree (widget + descendants); used to prevent dropping a tabs container into itself. */
  subtreeIds: Set<string>;
};

const DEFAULT_WIDGET_W = 6;
const DEFAULT_WIDGET_H = 8;
const MIN_CANVAS_ZOOM = 0.5;
const MAX_CANVAS_ZOOM = 2;
const CANVAS_ZOOM_STEP = 0.1;

function overlaps(a: PageGrid, b: PageGrid): boolean {
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}

type GridOccupant = { id: string; grid: PageGrid };

function canPlace(occupants: GridOccupant[], candidate: PageGrid): boolean {
  if (candidate.x < 0 || candidate.y < 0) return false;
  if (candidate.w < 1 || candidate.h < 1) return false;
  if (candidate.x + candidate.w > 12) return false;
  return !occupants.some((o) => overlaps(o.grid, candidate));
}

function canPlaceExcluding(occupants: GridOccupant[], excludeId: string, candidate: PageGrid): boolean {
  if (candidate.x < 0 || candidate.y < 0) return false;
  if (candidate.w < 1 || candidate.h < 1) return false;
  if (candidate.x + candidate.w > 12) return false;
  return !occupants.some((o) => o.id !== excludeId && overlaps(o.grid, candidate));
}

function findNearestVacant(occupants: GridOccupant[], desiredX: number, desiredY: number, w: number, h: number): PageGrid {
  const startX = Math.max(0, Math.min(12 - w, desiredX));
  const startY = Math.max(0, desiredY);

  for (let dist = 0; dist <= 80; dist += 1) {
    for (let dy = -dist; dy <= dist; dy += 1) {
      const y = startY + dy;
      if (y < 0) continue;
      const absDy = Math.abs(dy);
      const dx = dist - absDy;
      const candidates = dx === 0 ? [startX] : [startX - dx, startX + dx];
      for (const x of candidates) {
        if (x < 0 || x + w > 12) continue;
        const candidate = { x, y, w, h };
        if (canPlace(occupants, candidate)) return candidate;
      }
    }
  }

  for (let y = 0; y <= 10_000; y += 1) {
    for (let x = 0; x <= 12 - w; x += 1) {
      const candidate = { x, y, w, h };
      if (canPlace(occupants, candidate)) return candidate;
    }
  }

  return { x: 0, y: 0, w, h };
}

function findNearestVacantExcluding(
  occupants: GridOccupant[],
  excludeWidgetId: string,
  desiredX: number,
  desiredY: number,
  w: number,
  h: number
): PageGrid {
  const startX = Math.max(0, Math.min(12 - w, desiredX));
  const startY = Math.max(0, desiredY);

  for (let dist = 0; dist <= 80; dist += 1) {
    for (let dy = -dist; dy <= dist; dy += 1) {
      const y = startY + dy;
      if (y < 0) continue;
      const absDy = Math.abs(dy);
      const dx = dist - absDy;
      const candidates = dx === 0 ? [startX] : [startX - dx, startX + dx];
      for (const x of candidates) {
        if (x < 0 || x + w > 12) continue;
        const candidate = { x, y, w, h };
        if (canPlaceExcluding(occupants, excludeWidgetId, candidate)) return candidate;
      }
    }
  }

  return { x: startX, y: startY, w, h };
}

function mkWidgetId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `wid_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function toDraftSnapshot(input: {
  title: string;
  routeKey: string;
  description: string;
  layout: PageSpecV1["layout"];
  filters: PageFilter[];
  widgets: PageWidget[];
}): string {
  return JSON.stringify({
    title: input.title.trim(),
    route_key: input.routeKey.trim(),
    description: input.description.trim() || null,
    page_spec: {
      version: "page.v1",
      layout: {
        ...input.layout,
        cols: 12
      },
      ...(input.filters.length > 0 ? { filters: input.filters } : {}),
      widgets: input.widgets
    }
  });
}

function mkFilterId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `flt_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function isInteractionFilter(f: PageFilter): boolean {
  return (f.filter_kind ?? "slicer") === "interaction";
}

function clampCanvasZoom(value: number): number {
  return Math.min(MAX_CANVAS_ZOOM, Math.max(MIN_CANVAS_ZOOM, Number(value.toFixed(2))));
}

function isEditableTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (el.isContentEditable) return true;
  return Boolean(el.closest("[contenteditable='true']"));
}

export function BuildPageEditor() {
  const nav = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const { pageId = "" } = useParams<{ pageId: string }>();
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const suppressSurfaceClickUntilRef = useRef(0);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const saveTimeoutRef = useRef<number | null>(null);
  const [saveTrigger, setSaveTrigger] = useState(0);
  const isInitialLoadRef = useRef(true);
  const lastSavedSnapshotRef = useRef<string>("");
  const latestSnapshotRef = useRef<string>("");

  // ADR-0159 — the version this tab is editing, and the state it enters when
  // that version turns out to be stale. In a ref because the autosave closure
  // fires 150 ms after the render that would carry it as state.
  const pageTokenRef = useRef<string | null>(null);
  const [conflict, setConflict] = useState<WriteConflict | null>(null);
  // Read from inside the single-flight queue, which decides whether to run a
  // waiting save long after the render that produced this state.
  const conflictRef = useRef<WriteConflict | null>(null);
  useEffect(() => {
    conflictRef.current = conflict;
  }, [conflict]);

  const [pageIdState, setPageIdState] = useState("");
  const [title, setTitle] = useState("");
  const [routeKey, setRouteKey] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState<PageStatus>("draft");
  const [isViewerLanding, setIsViewerLanding] = useState(false);
  // ADR-0068 scope state — mirrors the page's current scope for the
  // <ScopeHeader>. Updated on fetch + after a promote/demote/clone.
  const [pageScope, setPageScope] = useState<import("../lib/scope.js").ContentScope>("shared");
  const [pageOwnerAccountId, setPageOwnerAccountId] = useState<string | null>(null);
  const [pageOwnerGroupId, setPageOwnerGroupId] = useState<string | null>(null);
  const [pageOwnerGroupName, setPageOwnerGroupName] = useState<string | null>(null);
  const [callerAccountId, setCallerAccountId] = useState<string | null>(null);
  const [callerRole, setCallerRole] = useState<"admin" | "builder" | "viewer">("viewer");
  const { groups: callerGroups } = useMyGroups();
  const [layout, setLayout] = useState<PageSpecV1["layout"]>({ cols: 12, rowHeight: 24, gap: 12, marginX: 12, marginY: 12 });
  const [filters, setFilters] = useState<PageFilter[]>([]);
  const [filterSelections, setFilterSelections] = useState<Record<string, Set<string>>>({});
  const [dateRangeSelections, setDateRangeSelections] = useState<DateRangeSelections>({});
  const [rowDriveUnselectedByWidget, setRowDriveUnselectedByWidget] = useState<Record<string, boolean>>({});
  const [isolateSelectionByWidget, setIsolateSelectionByWidget] = useState<Record<string, boolean>>({});
  const [dynamicChartFilters, setDynamicChartFilters] = useState<Array<{
    id: string;
    sourceWidgetId: string;
    sourceColumn: string;
    value: string;
    label: string;
  }>>([]);
  const [widgets, setWidgets] = useState<PageWidget[]>([]);

  const [bindableSources, setBindableSources] = useState<PageBindableSourceRow[]>([]);
  const [sourcePickerOpen, setSourcePickerOpen] = useState(false);
  const [sourcePickerInitial, setSourcePickerInitial] = useState<PageWidgetSource | undefined>(undefined);
  /** When set, picking a source updates this content widget (inspector Change…) instead of creating pendingWidget. */
  const [sourcePickerChangeWidgetId, setSourcePickerChangeWidgetId] = useState<string | null>(null);
  /** When adding from root canvas double-click: grid slot for the new widget. */
  const [pendingRootGrid, setPendingRootGrid] = useState<PageGrid | null>(null);
  /** When adding inside a tab panel: where to append the new content widget. */
  const [pendingTabTarget, setPendingTabTarget] = useState<{ containerId: string; tabId: string } | null>(null);
  const [editorActiveTabByContainer, setEditorActiveTabByContainer] = useState<Record<string, string>>({});

  const rootOccupants = useMemo(() => widgets.map((w) => ({ id: w.id, grid: w.grid })), [widgets]);
  const [specErrors, setSpecErrors] = useState<Record<string, string>>({});
  const [resolvedItems, setResolvedItems] = useState<Record<string, BatchResolveItem>>({});
  const [resolvedErrors, setResolvedErrors] = useState<Record<string, string>>({});
  /** The batch-resolve request itself failed (not a per-widget miss) — shown, never swallowed. */
  const [resolveFailure, setResolveFailure] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Inspector state
  const [focusedWidgetId, setFocusedWidgetId] = useState<string | null>(null);
  const [pendingWidget, setPendingWidget] = useState<PageContentWidget | null>(null);
  const [addAnchor, setAddAnchor] = useState<AddAnchor | null>(null);
  const [surfaceWidth, setSurfaceWidth] = useState(0);
  // ADR-0129 — inspector flyout overlay. Narrow default so the page stays
  // visible while configuring. Top measured via a callback ref (survives the
  // loading→loaded remount) + a document observer for later layout shifts.
  const [inspectorCollapsed, setInspectorCollapsed] = useState(false);
  const pageInspectorAreaElRef = useRef<HTMLDivElement | null>(null);
  const [pageInspectorFlyoutTop, setPageInspectorFlyoutTop] = useState(0);
  const measurePageInspectorTop = useCallback(() => {
    const el = pageInspectorAreaElRef.current;
    if (el) setPageInspectorFlyoutTop(el.getBoundingClientRect().top + window.scrollY);
  }, []);
  const pageInspectorAreaRef = useCallback((el: HTMLDivElement | null) => {
    pageInspectorAreaElRef.current = el;
    if (el) setPageInspectorFlyoutTop(el.getBoundingClientRect().top + window.scrollY);
  }, []);
  useEffect(() => {
    measurePageInspectorTop();
    window.addEventListener("resize", measurePageInspectorTop);
    window.addEventListener("scroll", measurePageInspectorTop, true);
    const ro = new ResizeObserver(measurePageInspectorTop);
    ro.observe(document.body);
    return () => {
      window.removeEventListener("resize", measurePageInspectorTop);
      window.removeEventListener("scroll", measurePageInspectorTop, true);
      ro.disconnect();
    };
  }, [measurePageInspectorTop]);
  const [inspectorTab, setInspectorTab] = useState<string>("page");

  // Interaction form state
  const [addingInteraction, setAddingInteraction] = useState(false);
  const [newInteractionTarget, setNewInteractionTarget] = useState("");
  const [newInteractionSourceCol, setNewInteractionSourceCol] = useState("");
  const [newInteractionTargetCol, setNewInteractionTargetCol] = useState("");

  const [dragState, setDragState] = useState<DragState | null>(null);
  const [dragPreview, setDragPreview] = useState<
    { widgetId: string; grid: PageGrid; targetSurface: SurfaceLocation } | null
  >(null);
  const [canvasZoom, setCanvasZoom] = useState(1);
  const dragPreviewRef = useRef<
    { widgetId: string; grid: PageGrid; targetSurface: SurfaceLocation } | null
  >(null);
  /**
   * Root surface min-height captured at drag start. While a drag is active we freeze
   * the root surface's min-height to this value so that a widget whose preview grid
   * is live-updating can't shrink the document — which would trigger browser scroll
   * clamping and cause a runaway feedback loop (shrink → scroll clamp → contentTop
   * shifts → row math changes → widget moves → shrinks more).
   */
  const [frozenSurfaceMinHeight, setFrozenSurfaceMinHeight] = useState<number | null>(null);
  const [sourcesPopoverWidgetId, setSourcesPopoverWidgetId] = useState<string | null>(null);

  const inspectorSelection = useMemo(():
    | { kind: "content"; widget: PageContentWidget }
    | { kind: "tabs"; widget: PageTabsWidget }
    | null => {
    if (!focusedWidgetId) return null;
    const leaf = findContentWidgetById(widgets, focusedWidgetId);
    if (leaf) return { kind: "content", widget: leaf };
    const tabsW = findTabsWidgetById(widgets, focusedWidgetId);
    if (tabsW) return { kind: "tabs", widget: tabsW };
    return null;
  }, [widgets, focusedWidgetId]);
  const inspectorContent = inspectorSelection?.kind === "content" ? inspectorSelection.widget : null;
  const inspectorTabs = inspectorSelection?.kind === "tabs" ? inspectorSelection.widget : null;
  const modalWidget = pendingWidget;
  const isAddingNew = pendingWidget !== null;
  const zoomPercent = Math.round(canvasZoom * 100);

  const vizSourceNameByKey = useMemo(() => {
    const out: Record<string, string> = {};
    for (const src of bindableSources) {
      for (const t of src.targets) {
        const key = `op_node:${src.operation_id}:${t.node_id}`;
        const label = t.name ? `${src.operation_name} / ${t.name}` : src.operation_name;
        out[key] = label;
      }
    }
    return out;
  }, [bindableSources]);

  // Reset tab when inspector context changes (widget ↔ page)
  useEffect(() => {
    setInspectorTab(focusedWidgetId ? "settings" : "page");
    setAddingInteraction(false);
  }, [focusedWidgetId]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [pageData, vizSourceData, meData] = await Promise.all([
        apiFetch<{ page: PageRecord }>(`/api/tenant/pages/${encodeURIComponent(pageId)}`),
        apiFetch<{ sources: PageBindableSourceRow[] }>("/api/tenant/operations/visualization-sources"),
        apiFetch<{ user: { id: string; entitlement?: string } }>("/api/tenant/me").catch(() => null)
      ]);
      if (meData) {
        setCallerAccountId(meData.user.id);
        const e = meData.user.entitlement;
        setCallerRole(e === "admin" ? "admin" : e === "builder" ? "builder" : "viewer");
      }
      const page = pageData.page;
      // ADR-0159 — the base every save from this tab will be measured against.
      pageTokenRef.current = page.updated_at ?? null;
      setConflict(null);
      setPageIdState(page.id);
      setTitle(page.title);
      setRouteKey(page.route_key);
      setDescription(page.description ?? "");
      setStatus(page.status);
      setIsViewerLanding(page.is_viewer_landing);
      if (page.scope) setPageScope(page.scope);
      setPageOwnerAccountId(page.owner_account_id ?? null);
      setPageOwnerGroupId(page.owner_group_id ?? null);
      setPageOwnerGroupName(page.owner_group_name ?? null);
      setLayout(page.page_spec.layout);
      setFilters(page.page_spec.filters ?? []);
      setFilterSelections({});
      setDynamicChartFilters([]);
      setWidgets(page.page_spec.widgets);
      setBindableSources(vizSourceData.sources);
      lastSavedSnapshotRef.current = toDraftSnapshot({
        title: page.title,
        routeKey: page.route_key,
        description: page.description ?? "",
        layout: page.page_spec.layout,
        filters: page.page_spec.filters ?? [],
        widgets: page.page_spec.widgets
      });
      isInitialLoadRef.current = false;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    isInitialLoadRef.current = true;
    void load();
  }, [pageId]);

  const resolveWidgets = useCallback(async () => {
    const widgetSources = sourcesFromWidgetsUnique(widgets);
    const slicerSources = filters
      .map((f) => f.source_viz)
      .filter((s): s is PageWidgetSource => !!s);
    const seen = new Set<string>();
    const sources: PageWidgetSource[] = [];
    for (const s of [...widgetSources, ...slicerSources]) {
      const key = widgetSourceKey(s);
      if (seen.has(key)) continue;
      seen.add(key);
      sources.push(s);
    }
    if (sources.length === 0) {
      setResolvedItems({});
      setResolvedErrors({});
      setResolveFailure(null);
      return;
    }
    try {
      const data = await apiFetch<{ items: BatchResolveItem[]; errors: Array<{ source_key: string; code: string; message: string }> }>(
        "/api/tenant/visualizations/batch-resolve",
        // ATLAS-SEC-064: name the page being edited, same as the viewer path.
        { method: "POST", body: { sources, page_id: pageIdState } }
      );
      // Keyed by the source keys this editor computes, not only the hub's —
      // a port-less render widget is keyed on render_out by the hub and on
      // tabular_out here. See indexBatchResolve.
      const indexed = indexBatchResolve(sources, data);
      const errs: Record<string, string> = {};
      for (const [k, e] of Object.entries(indexed.errors)) errs[k] = e.message;
      setResolvedItems(indexed.items);
      setResolvedErrors(errs);
      setResolveFailure(null);
    } catch (e) {
      // A failed request used to be indistinguishable from "never run": every
      // widget went blank with the same label. Keep what was showing and say
      // what happened.
      setResolveFailure(e instanceof Error ? e.message : String(e));
    }
  }, [widgets, filters, pageIdState]);

  useEffect(() => {
    void resolveWidgets();
  }, [resolveWidgets]);

  // While any widget's step is being computed in the background (a never-run
  // or stale step the hub healed on open), re-resolve until the row lands.
  const preparingPollCountRef = useRef(0);
  const anyPreparing = useMemo(() => Object.values(resolvedItems).some(isPreparingItem), [resolvedItems]);
  useEffect(() => {
    if (!anyPreparing) {
      preparingPollCountRef.current = 0;
      return;
    }
    if (preparingPollCountRef.current >= PREPARING_POLL_MAX) return;
    const handle = window.setTimeout(() => {
      preparingPollCountRef.current += 1;
      void resolveWidgets();
    }, PREPARING_POLL_MS);
    return () => window.clearTimeout(handle);
  }, [anyPreparing, resolvedItems, resolveWidgets]);

  const refreshData = useCallback(async () => {
    if (!pageIdState) return;
    setRefreshing(true);
    try {
      const result = await refreshPageData(pageIdState);
      await resolveWidgets();
      for (const t of refreshToastMessages(result)) toast.push(t);
      if (result.skipped.length === 0) {
        toast.push({ kind: "success", message: "Data refreshed." });
      }
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setRefreshing(false);
    }
  }, [pageIdState, resolveWidgets, toast]);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (isEditableTarget(e.target)) return;
      if (!(e.metaKey || e.ctrlKey) || e.altKey) return;
      if (e.key === "0") {
        e.preventDefault();
        setCanvasZoom(1);
        return;
      }
      if (e.key === "+" || e.key === "=" || e.code === "NumpadAdd") {
        e.preventDefault();
        setCanvasZoom((current) => clampCanvasZoom(current + CANVAS_ZOOM_STEP));
        return;
      }
      if (e.key === "-" || e.key === "_" || e.code === "NumpadSubtract") {
        e.preventDefault();
        setCanvasZoom((current) => clampCanvasZoom(current - CANVAS_ZOOM_STEP));
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const sourceRenderModelByFilterId = useMemo(() => {
    const m: Record<string, RenderModel | undefined> = {};
    for (const f of filters) {
      const key = filterSourceKey(f, widgets);
      m[f.id] = key ? resolvedItems[key]?.last?.renderModel : undefined;
    }
    return m;
  }, [filters, widgets, resolvedItems]);

  const effectiveFilterSelections = useMemo(
    () =>
      effectiveFilterSelectionsForRender(filters, filterSelections, rowDriveUnselectedByWidget, sourceRenderModelByFilterId),
    [filters, filterSelections, rowDriveUnselectedByWidget, sourceRenderModelByFilterId]
  );

  function setContentPatch(widgetId: string, patch: Partial<PageContentWidget>) {
    setWidgets((current) =>
      updateContentWidgetById(current, widgetId, (cw) => ({
        ...cw,
        ...patch,
        ...(patch.grid ? { grid: { ...cw.grid, ...patch.grid } } : {}),
        ...(patch.source ? { source: { ...cw.source, ...patch.source } } : {})
      }))
    );
  }

  function setContentGridPatch(widgetId: string, patch: Partial<PageGrid>) {
    setWidgets((current) =>
      updateContentWidgetById(current, widgetId, (cw) => ({ ...cw, grid: { ...cw.grid, ...patch } }))
    );
  }

  function setTabsPatch(containerId: string, patch: Partial<PageTabsWidget>) {
    setWidgets((current) =>
      updateTabsWidgetById(current, containerId, (tw) => ({
        ...tw,
        ...patch,
        ...(patch.grid ? { grid: { ...tw.grid, ...patch.grid } } : {})
      }))
    );
  }

  function setTabsGridPatch(containerId: string, patch: Partial<PageGrid>) {
    setWidgets((current) =>
      updateTabsWidgetById(current, containerId, (tw) => ({ ...tw, grid: { ...tw.grid, ...patch } }))
    );
  }

  /** Open wide source picker, then narrow layout modal. Root or a specific tab panel. */
  function addWidgetAt(
    desiredX: number,
    desiredY: number,
    tabTarget?: { containerId: string; tabId: string } | null
  ) {
    const hasAny = bindableSources.some((s) => s.targets.length > 0);
    if (!hasAny) {
      toast.push({ kind: "error", message: "Create an operation with a visualization or AI provider node first." });
      return;
    }
    if (tabTarget) {
      const tabsW = findTabsWidgetById(widgets, tabTarget.containerId);
      const panel = tabsW?.tabs.find((t) => t.id === tabTarget.tabId)?.widgets ?? [];
      const occ = panel.map((cw) => ({ id: cw.id, grid: cw.grid }));
      const grid = findNearestVacant(occ, desiredX, desiredY, DEFAULT_WIDGET_W, DEFAULT_WIDGET_H);
      setPendingRootGrid(grid);
      setPendingTabTarget(tabTarget);
    } else {
      const grid = findNearestVacant(rootOccupants, desiredX, desiredY, DEFAULT_WIDGET_W, DEFAULT_WIDGET_H);
      setPendingRootGrid(grid);
      setPendingTabTarget(null);
    }
    setSourcePickerInitial(undefined);
    setSourcePickerChangeWidgetId(null);
    setSourcePickerOpen(true);
    setAddAnchor(null);
  }

  function commitPendingWidget() {
    if (!pendingWidget) return;
    const cw = pendingWidget;
    if (pendingTabTarget) {
      const { containerId, tabId } = pendingTabTarget;
      setWidgets((current) => appendContentWidgetToTab(current, containerId, tabId, cw));
    } else {
      setWidgets((current) => [...current, cw]);
    }
    setFocusedWidgetId(cw.id);
    setPendingWidget(null);
    setPendingRootGrid(null);
    setPendingTabTarget(null);
    requestSave();
  }

  function cancelPendingWidget() {
    setPendingWidget(null);
    setPendingRootGrid(null);
    setPendingTabTarget(null);
  }

  function closeSourcePicker() {
    setSourcePickerOpen(false);
    setSourcePickerChangeWidgetId(null);
    setSourcePickerInitial(undefined);
    setPendingRootGrid(null);
    setPendingTabTarget(null);
  }

  function handleSourcePicked(source: PageWidgetSource) {
    if (sourcePickerChangeWidgetId) {
      setContentPatch(sourcePickerChangeWidgetId, { source });
      setSourcePickerChangeWidgetId(null);
      setSourcePickerOpen(false);
      requestSave();
      return;
    }
    const panelWidgets =
      pendingTabTarget !== null
        ? (findTabsWidgetById(widgets, pendingTabTarget.containerId)?.tabs.find((t) => t.id === pendingTabTarget.tabId)?.widgets ?? [])
        : [];
    const occ = pendingTabTarget ? panelWidgets.map((cw) => ({ id: cw.id, grid: cw.grid })) : rootOccupants;
    const grid = pendingRootGrid ?? findNearestVacant(occ, 0, 0, DEFAULT_WIDGET_W, DEFAULT_WIDGET_H);
    setPendingWidget({
      id: mkWidgetId(),
      source,
      grid
    });
    setSourcePickerOpen(false);
    setPendingRootGrid(null);
    // Keep pendingTabTarget until commitPendingWidget / cancelPendingWidget / closeSourcePicker
  }

  function addTabbedContainerAt(desiredX: number, desiredY: number, tabTarget?: { containerId: string; tabId: string } | null) {
    if (tabTarget) {
      const tabsW = findTabsWidgetById(widgets, tabTarget.containerId);
      const panel = tabsW?.tabs.find((t) => t.id === tabTarget.tabId)?.widgets ?? [];
      const occ = panel.map((w) => ({ id: w.id, grid: w.grid }));
      const grid = findNearestVacant(occ, desiredX, desiredY, 12, 10);
      const tw = createDefaultTabsWidget({ id: mkWidgetId(), grid });
      setWidgets((c) => appendWidgetToTab(c, tabTarget.containerId, tabTarget.tabId, tw));
      setFocusedWidgetId(tw.id);
    } else {
      const grid = findNearestVacant(rootOccupants, desiredX, desiredY, 12, 10);
      const tw = createDefaultTabsWidget({ id: mkWidgetId(), grid });
      setWidgets((c) => [...c, tw]);
      setFocusedWidgetId(tw.id);
    }
    setAddAnchor(null);
    requestSave();
  }

  function updatePendingTitleOverride(val: string | undefined) {
    setPendingWidget((prev) => prev ? { ...prev, title_override: val } : prev);
  }

  function updatePendingGrid(patch: Partial<PageGrid>) {
    setPendingWidget((prev) => prev ? { ...prev, grid: { ...prev.grid, ...patch } } : prev);
  }

  function duplicateWidget(widgetId: string) {
    const original = findContentWidgetById(widgets, widgetId);
    if (!original) return;
    const parent = findTabParentForContent(widgets, widgetId);
    const occ = parent ? parent.panel.map((cw) => ({ id: cw.id, grid: cw.grid })) : rootOccupants;
    const grid = findNearestVacant(occ, original.grid.x, original.grid.y + 1, original.grid.w, original.grid.h);
    const next: PageContentWidget = {
      id: mkWidgetId(),
      source: { ...original.source },
      title_override: original.title_override,
      grid
    };
    if (parent) {
      setWidgets((cur) => appendContentWidgetToTab(cur, parent.containerId, parent.tabId, next));
    } else {
      setWidgets((current) => [...current, next]);
    }
    setFocusedWidgetId(next.id);
    setAddingInteraction(false);
    requestSave();
  }

  function removeWidget(widgetId: string) {
    setWidgets((current) => removeWidgetFromPage(current, widgetId));
    setFilters((current) =>
      current
        .filter((f) => f.source_widget_id !== widgetId)
        .map((f) => ({ ...f, bindings: f.bindings.filter((b) => b.widget_id !== widgetId) }))
    );
    if (focusedWidgetId === widgetId) {
      setFocusedWidgetId(null);
      setAddingInteraction(false);
    }
    requestSave();
  }

  function removeTabFromContainer(containerId: string, tabId: string) {
    const tw = findTabsWidgetById(widgets, containerId);
    const tabPanel = tw?.tabs.find((t) => t.id === tabId);
    if (!tw || !tabPanel || tw.tabs.length <= 1) return;

    const removedIds = collectWidgetIdsInPanelTree(tabPanel.widgets);
    const nextWidgets = removeTabPanelFromTabsWidget(widgets, containerId, tabId);
    if (!nextWidgets) return;

    const removedArr = Array.from(removedIds);
    const nextFilters = filters
      .filter((f) => !removedIds.has(f.source_widget_id))
      .map((f) => ({ ...f, bindings: f.bindings.filter((b) => !removedIds.has(b.widget_id)) }))
      .filter((f) => f.bindings.length > 0);
    const keptFilterIds = new Set(nextFilters.map((f) => f.id));

    setWidgets(nextWidgets);
    setFilters(nextFilters);
    setFilterSelections((prev) => {
      const next = { ...prev };
      for (const f of filters) {
        if (!keptFilterIds.has(f.id)) delete next[f.id];
      }
      return next;
    });
    setDynamicChartFilters((prev) => prev.filter((c) => !removedIds.has(c.sourceWidgetId)));
    setRowDriveUnselectedByWidget((prev) => {
      const next = { ...prev };
      for (const id of removedArr) delete next[id];
      return next;
    });
    setIsolateSelectionByWidget((prev) => {
      const next = { ...prev };
      for (const id of removedArr) delete next[id];
      return next;
    });
    if (sourcesPopoverWidgetId && removedIds.has(sourcesPopoverWidgetId)) {
      setSourcesPopoverWidgetId(null);
    }
    setEditorActiveTabByContainer((prev) => {
      const next = { ...prev };
      for (const id of removedArr) delete next[id];
      if (next[containerId] === tabId) {
        const newTw = findTabsWidgetById(nextWidgets, containerId);
        const first = newTw?.tabs[0]?.id;
        if (first) next[containerId] = first;
      }
      return next;
    });
    setPendingTabTarget((pt) => {
      if (!pt) return pt;
      if (removedIds.has(pt.containerId)) return null;
      if (pt.containerId === containerId && pt.tabId === tabId) {
        const newTw = findTabsWidgetById(nextWidgets, containerId);
        const first = newTw?.tabs[0]?.id;
        return first ? { containerId, tabId: first } : null;
      }
      return pt;
    });
    if (focusedWidgetId && removedIds.has(focusedWidgetId)) {
      setFocusedWidgetId(null);
      setAddingInteraction(false);
    }
    requestSave();
  }

  function columnsForSourceKey(key: string): string[] {
    const item = resolvedItems[key];
    if (!item?.last?.renderModel) return [];
    const rm = item.last.renderModel;
    if (rm.kind === "ai_content" || rm.kind === "ai_production_prompt") return [];
    if (rm.detailData && rm.detailData.columns.length > 0) return rm.detailData.columns;
    if (rm.kind === "tantable") return rm.columns.map((c) => c.key);
    if (rm.kind === "echarts") {
      const opt = rm.option as Record<string, unknown> | undefined;
      if (!opt) return [];
      const ds = Array.isArray(opt.dataset) ? opt.dataset[0] as Record<string, unknown> | undefined : opt.dataset as Record<string, unknown> | undefined;
      if (ds && Array.isArray(ds.dimensions)) return (ds.dimensions as unknown[]).filter((d): d is string => typeof d === "string");
      if (ds && Array.isArray(ds.source) && (ds.source as unknown[]).length > 0) {
        const first = (ds.source as unknown[])[0];
        if (Array.isArray(first)) return first.map(String);
      }
      return [];
    }
    return [];
  }

  function columnsForWidget(widgetId: string): string[] {
    const widget = findContentWidgetById(widgets, widgetId);
    if (!widget) return [];
    return columnsForSourceKey(widgetSourceKey(widget.source));
  }

  function columnsForFilter(filter: PageFilter): string[] {
    const key = filterSourceKey(filter, widgets);
    return key ? columnsForSourceKey(key) : [];
  }

  function addFilter() {
    const flat = flattenContentWidgets(widgets);
    const firstWidget = flat[0];
    const cols = firstWidget ? columnsForWidget(firstWidget.id) : [];
    const newFilter: PageFilter = {
      id: mkFilterId(),
      label: cols[0] ?? "Filter",
      multi: true,
      filter_kind: "slicer",
      source_widget_id: firstWidget?.id ?? "",
      source_column: cols[0] ?? "",
      bindings: firstWidget ? [{ widget_id: firstWidget.id, kind: "column", column: cols[0] ?? "" }] : []
    };
    setFilters((current) => [...current, newFilter]);
  }

  function updateFilter(filterId: string, patch: Partial<PageFilter>) {
    setFilters((current) => current.map((f) => (f.id === filterId ? { ...f, ...patch } : f)));
  }

  function removeFilter(filterId: string) {
    setFilters((current) => current.filter((f) => f.id !== filterId));
    setFilterSelections((prev) => {
      const next = { ...prev };
      delete next[filterId];
      return next;
    });
    requestSave();
  }

  function addInteraction(sourceWidgetId: string, targetWidgetId: string, sourceColumn: string, targetColumn: string) {
    setFilters((current) => {
      const existingForSource = current.find((f) => f.source_widget_id === sourceWidgetId && f.source_column === sourceColumn);
      if (existingForSource) {
        const alreadyBound = existingForSource.bindings.some(
          (b) => b.widget_id === targetWidgetId && b.kind === "column" && b.column === targetColumn
        );
        if (alreadyBound) return current;
        return current.map((f) =>
          f.id === existingForSource.id
            ? { ...f, bindings: [...f.bindings, { widget_id: targetWidgetId, kind: "column" as const, column: targetColumn }] }
            : f
        );
      }
      const newFilter: PageFilter = {
        id: mkFilterId(),
        label: sourceColumn,
        multi: true,
        filter_kind: "interaction",
        source_widget_id: sourceWidgetId,
        source_column: sourceColumn,
        bindings: [{ widget_id: targetWidgetId, kind: "column", column: targetColumn }]
      };
      return [...current, newFilter];
    });
  }

  function removeInteraction(filterId: string, targetWidgetId: string) {
    setFilters((current) => {
      return current
        .map((f) => {
          if (f.id !== filterId) return f;
          const newBindings = f.bindings.filter((b) => b.widget_id !== targetWidgetId);
          return { ...f, bindings: newBindings };
        })
        .filter((f) => f.bindings.length > 0);
    });
    requestSave();
  }

  function toggleFilterValue(filterId: string, value: string, multi: boolean) {
    setFilterSelections((prev) => nextToggleFilterValue(prev, filterId, value, multi));
  }

  function selectAllFilterValues(filterId: string, values: string[]) {
    setFilterSelections((prev) => nextSelectAllFilterValues(prev, filterId, values));
  }

  function clearFilterSelection(filterId: string) {
    setFilterSelections((prev) => nextClearFilterSelection(prev, filterId));
  }

  function clearAllFilterSelections() {
    setFilterSelections(nextClearAllFilterSelections());
    setDateRangeSelections(nextClearAllDateRangeSelections());
    setDynamicChartFilters([]);
  }

  function handleTanTableRowSync(sourceWidgetId: string, event: TanTableRowSelectEvent) {
    setFilterSelections((prev) => applyTanTableRowSelectToSelections(prev, filters, sourceWidgetId, event));
  }

  function applyChartDynamicFilter(sourceWidgetId: string, point: { value: string; column?: string }) {
    const hasConfiguredBinding = filters.some(
      (filter) => filter.source_widget_id === sourceWidgetId && filter.bindings.length > 0
    );
    if (hasConfiguredBinding) {
      setFilterSelections((prev) => nextApplyChartDynamicFilter(prev, filters, sourceWidgetId, point.value));
      return;
    }
    const column = point.column;
    if (!column) return;
    setDynamicChartFilters((prev) =>
      nextToggleDynamicChartFilter(prev, {
        sourceWidgetId,
        sourceColumn: column,
        value: point.value,
        label: `${column}: ${point.value}`
      })
    );
  }

  function onSurfaceClick(e: React.MouseEvent<HTMLDivElement>) {
    if (Date.now() < suppressSurfaceClickUntilRef.current) return;
    if (dragState) return;
    if (e.target !== e.currentTarget) return;
    // Single-click on empty canvas → go to page context, dismiss any open prompt
    setFocusedWidgetId(null);
    setAddAnchor(null);
    setAddingInteraction(false);
  }

  function onSurfaceDoubleClick(e: React.MouseEvent<HTMLDivElement>) {
    if (Date.now() < suppressSurfaceClickUntilRef.current) return;
    if (dragState) return;
    if (e.target !== e.currentTarget) return;
    if (!surfaceRef.current) return;

    const rect = surfaceRef.current.getBoundingClientRect();
    const marginX = layout.marginX ?? 0;
    const marginY = layout.marginY ?? 0;
    const localX = (e.clientX - rect.left) / canvasZoom - marginX;
    const localY = (e.clientY - rect.top) / canvasZoom - marginY;

    const usableWidth = Math.max(1, rect.width / canvasZoom - marginX * 2);
    const colWidth = (usableWidth - layout.gap * 11) / 12;
    const x = Math.max(0, Math.min(11, Math.floor(localX / Math.max(1, colWidth + layout.gap))));
    const y = Math.max(0, Math.floor(localY / Math.max(1, layout.rowHeight + layout.gap)));

    const promptH = 40;
    const gutter = 8;
    const viewTop = e.clientY + promptH + gutter > window.innerHeight
      ? Math.max(gutter, e.clientY - promptH - gutter)
      : e.clientY + gutter;
    const viewLeft = Math.max(gutter, Math.min(e.clientX, window.innerWidth - 200));
    setAddAnchor({ x, y, left: viewLeft, top: viewTop });
    setFocusedWidgetId(null);
  }

  /** Select this tabs widget in the inspector for almost any click on its chrome or panel (capture phase). */
  function focusTabsWidgetFromPointerCapture(e: React.PointerEvent<HTMLElement>, widgetId: string) {
    if (Date.now() < suppressSurfaceClickUntilRef.current) return;
    if (dragState) return;
    const root = e.currentTarget;
    const t = e.target as HTMLElement | null;
    if (!t) return;
    const innerTabs = t.closest(".pageTabsContainer");
    if (innerTabs && innerTabs !== root) return;
    const ourHeader = root.querySelector(":scope > .pageWidgetHeader");
    const clickedHeader = t.closest(".pageWidgetHeader");
    if (clickedHeader && root.contains(clickedHeader) && clickedHeader !== ourHeader) return;
    setFocusedWidgetId(widgetId);
    setAddAnchor(null);
    setAddingInteraction(false);
  }

  function onNestedTabSurfaceDoubleClick(
    e: React.MouseEvent<HTMLDivElement>,
    containerId: string,
    tabId: string
  ) {
    if (Date.now() < suppressSurfaceClickUntilRef.current) return;
    if (dragState) return;
    if (e.target !== e.currentTarget) return;
    e.stopPropagation();
    const el = e.currentTarget;
    const rect = el.getBoundingClientRect();
    const pad = layout.gap * canvasZoom;
    const localX = (e.clientX - rect.left - pad) / canvasZoom;
    const localY = (e.clientY - rect.top - pad) / canvasZoom;
    const usableWidth = Math.max(1, (rect.width - pad * 2) / canvasZoom);
    const colWidth = (usableWidth - layout.gap * 11) / 12;
    const x = Math.max(0, Math.min(11, Math.floor(localX / Math.max(1, colWidth + layout.gap))));
    const y = Math.max(0, Math.floor(localY / Math.max(1, layout.rowHeight + layout.gap)));

    const promptH = 40;
    const gutter = 8;
    const viewTop = e.clientY + promptH + gutter > window.innerHeight
      ? Math.max(gutter, e.clientY - promptH - gutter)
      : e.clientY + gutter;
    const viewLeft = Math.max(gutter, Math.min(e.clientX, window.innerWidth - 200));
    setAddAnchor({ x, y, left: viewLeft, top: viewTop, tabTarget: { containerId, tabId } });
    setFocusedWidgetId(containerId);
  }

  function getGridUnits() {
    if (!surfaceRef.current) return null;
    const rect = surfaceRef.current.getBoundingClientRect();
    const marginX = layout.marginX ?? 0;
    const usableWidth = Math.max(1, rect.width / canvasZoom - marginX * 2);
    const colUnit = Math.max(1, (usableWidth - layout.gap * 11) / 12 + layout.gap);
    const rowUnit = Math.max(1, layout.rowHeight + layout.gap);
    return { colUnit, rowUnit };
  }

  /**
   * Grid unit size for a specific surface element. Nested tab panels have their own width + padding
   * (gap, no marginX) so we compute from their content box directly.
   */
  function getGridUnitsForSurface(el: HTMLElement): { colUnit: number; rowUnit: number; contentLeft: number; contentTop: number } {
    const rect = el.getBoundingClientRect();
    const isRoot = el.getAttribute("data-pb-surface") === "root";
    const marginX = isRoot ? (layout.marginX ?? 0) : layout.gap;
    const marginY = isRoot ? (layout.marginY ?? 0) : layout.gap;
    const usableWidth = Math.max(1, rect.width / canvasZoom - marginX * 2);
    const colUnit = Math.max(1, (usableWidth - layout.gap * 11) / 12 + layout.gap);
    const rowUnit = Math.max(1, layout.rowHeight + layout.gap);
    return {
      colUnit,
      rowUnit,
      contentLeft: rect.left + marginX * canvasZoom,
      contentTop: rect.top + marginY * canvasZoom
    };
  }

  /** Convert a viewport point into the grid column/row of the surface under that point. */
  function pointToGridOnSurface(el: HTMLElement, clientX: number, clientY: number): { col: number; row: number } {
    const u = getGridUnitsForSurface(el);
    const col = Math.floor(((clientX - u.contentLeft) / canvasZoom) / u.colUnit);
    const row = Math.floor(((clientY - u.contentTop) / canvasZoom) / u.rowUnit);
    return { col, row };
  }

  /** Walk up from `elementFromPoint` to find the nearest page-builder surface. */
  function detectSurfaceAtPoint(clientX: number, clientY: number): { el: HTMLElement; loc: SurfaceLocation } | null {
    const el = document.elementFromPoint(clientX, clientY) as HTMLElement | null;
    if (!el) return null;
    const surface = el.closest<HTMLElement>("[data-pb-surface]");
    if (!surface) return null;
    const kind = surface.getAttribute("data-pb-surface");
    if (kind === "root") return { el: surface, loc: { kind: "root" } };
    if (kind === "tab") {
      const containerId = surface.getAttribute("data-pb-container-id") ?? "";
      const tabId = surface.getAttribute("data-pb-tab-id") ?? "";
      if (!containerId || !tabId) return null;
      return { el: surface, loc: { kind: "tab", containerId, tabId } };
    }
    return null;
  }

  /** Siblings (for overlap checks) on a given surface, optionally excluding the dragged widget if it already lives there. */
  function occupantsForSurface(loc: SurfaceLocation): GridOccupant[] {
    if (loc.kind === "root") {
      return widgets.map((w) => ({ id: w.id, grid: w.grid }));
    }
    const container = findTabsWidgetById(widgets, loc.containerId);
    if (!container) return [];
    const tab = container.tabs.find((t) => t.id === loc.tabId);
    if (!tab) return [];
    return tab.widgets.map((w) => ({ id: w.id, grid: w.grid }));
  }

  /** Where in the tree does a given widget live? */
  function locateWidgetSurface(widgetId: string): SurfaceLocation | null {
    const parent = findTabParentForContent(widgets, widgetId);
    if (parent) return { kind: "tab", containerId: parent.containerId, tabId: parent.tabId };
    // Root-level widget (content or tabs container)
    if (widgets.some((w) => w.id === widgetId)) return { kind: "root" };
    return null;
  }

  function sameSurface(a: SurfaceLocation, b: SurfaceLocation): boolean {
    if (a.kind !== b.kind) return false;
    if (a.kind === "root") return true;
    return a.containerId === (b as { containerId: string }).containerId && a.tabId === (b as { tabId: string }).tabId;
  }

  /**
   * Live grid for a widget during drag. Returns the drag preview's grid ONLY when the
   * drag target surface matches the source surface (same-surface move). For cross-surface
   * drags the widget stays pinned to its original grid — the drop-zone ghost on the target
   * surface shows where it will land, and the actual move happens at pointer-up.
   */
  function previewGridFor(widgetGrid: PageGrid, widgetId: string): PageGrid {
    if (!dragPreview || dragPreview.widgetId !== widgetId) return widgetGrid;
    if (!dragState) return widgetGrid;
    if (sameSurface(dragPreview.targetSurface, dragState.sourceSurface)) return dragPreview.grid;
    return widgetGrid;
  }

  function beginDrag(widgetId: string, mode: DragMode, e: React.PointerEvent) {
    e.preventDefault();
    e.stopPropagation();
    const grid = findWidgetGridById(widgets, widgetId);
    const occupants = findSiblingOccupantsForWidget(widgets, widgetId);
    const sourceSurface = locateWidgetSurface(widgetId);
    if (!grid || !occupants || !sourceSurface) return;

    // Compute grab offset (in columns/rows) so the grab point stays under the cursor across surfaces.
    const sourceEl =
      sourceSurface.kind === "root"
        ? surfaceRef.current
        : document.querySelector<HTMLElement>(
            `[data-pb-surface="tab"][data-pb-container-id="${sourceSurface.containerId}"][data-pb-tab-id="${sourceSurface.tabId}"]`
          );
    let grabOffsetCol = 0;
    let grabOffsetRow = 0;
    if (sourceEl) {
      const pg = pointToGridOnSurface(sourceEl, e.clientX, e.clientY);
      grabOffsetCol = pg.col - grid.x;
      grabOffsetRow = pg.row - grid.y;
    }

    // Collect dragged subtree ids so we can block dropping a tabs container into itself/descendants.
    const subtreeIds = new Set<string>([widgetId]);
    const tabsNode = findTabsWidgetById(widgets, widgetId);
    if (tabsNode) {
      for (const id of collectWidgetIdsInPanelTree(tabsNode.tabs.flatMap((t) => t.widgets))) {
        subtreeIds.add(id);
      }
    }

    setAddAnchor(null);
    // Freeze root surface min-height at the current rendered height so the document
    // can't shrink mid-drag and cause browser scroll clamping. Includes a buffer so the
    // user has room to drag further down than the current content extent.
    if (surfaceRef.current) {
      const currentHeight = surfaceRef.current.offsetHeight;
      setFrozenSurfaceMinHeight(Math.max(currentHeight, 400));
    }
    setDragState({
      widgetId,
      mode,
      startClientX: e.clientX,
      startClientY: e.clientY,
      startScrollY: window.scrollY,
      origin: { ...grid },
      occupants,
      sourceSurface,
      grabOffsetCol,
      grabOffsetRow,
      subtreeIds
    });
    const initialPreview = { widgetId, grid: { ...grid }, targetSurface: sourceSurface };
    dragPreviewRef.current = initialPreview;
    setDragPreview(initialPreview);
  }

  useEffect(() => {
    if (!dragState) return;
    const ds = dragState;
    let lastPointerX = ds.startClientX;
    let lastPointerY = ds.startClientY;
    /** Calm linear auto-scroll: viewport-edge pan to extend drag/resize reach. */
    const EDGE_ZONE = 60;
    const MAX_SCROLL_PX_PER_SEC = 250;
    let rafId: number | null = null;
    let lastFrameTime: number | null = null;
    const resizeGrowsDown = ds.mode === "move" || ds.mode === "resize-s" || ds.mode === "resize-se";
    const resizeGrowsUp = ds.mode === "move" || ds.mode === "resize-n" || ds.mode === "resize-nw";

    function computePreview() {
      // ── Move mode: pointer-absolute positioning so we can cross surfaces ──
      if (ds.mode === "move") {
        // Detect which surface the pointer is over. We always need a target — if the pointer
        // happens to sit over the dragged widget itself (the ghost), or over a gap, fall back
        // to the last known target, then to the source surface.
        let target = detectSurfaceAtPoint(lastPointerX, lastPointerY);
        if (!target) {
          const fallback = dragPreviewRef.current?.targetSurface ?? ds.sourceSurface;
          const fallbackEl =
            fallback.kind === "root"
              ? surfaceRef.current
              : document.querySelector<HTMLElement>(
                  `[data-pb-surface="tab"][data-pb-container-id="${fallback.containerId}"][data-pb-tab-id="${fallback.tabId}"]`
                );
          if (fallbackEl) target = { el: fallbackEl, loc: fallback };
        }
        if (!target) return;

        // Cycle guard: dragging a tabs container into its own subtree is rejected. Hold the
        // previous valid target while the pointer hovers the illegal area.
        if (target.loc.kind === "tab" && ds.subtreeIds.has(target.loc.containerId)) {
          return;
        }

        const pg = pointToGridOnSurface(target.el, lastPointerX, lastPointerY);
        const desiredX = Math.max(0, Math.min(12 - ds.origin.w, pg.col - ds.grabOffsetCol));
        const desiredY = Math.max(0, pg.row - ds.grabOffsetRow);

        const nextPreview = {
          widgetId: ds.widgetId,
          grid: { ...ds.origin, x: desiredX, y: desiredY },
          targetSurface: target.loc
        };
        dragPreviewRef.current = nextPreview;
        setDragPreview(nextPreview);
        return;
      }

      // ── Resize modes: stay on source surface, delta math against ds.occupants ──
      const units = getGridUnits();
      if (!units) return;
      // Scroll compensation lets auto-scroll extend resize reach: when the pointer is pinned
      // at the viewport bottom and the page scrolls to expose new rows, scrollY grows and
      // so should the computed dy. Without this, resize-south is capped at one viewport.
      const scrollComp = window.scrollY - ds.startScrollY;
      const dx = (lastPointerX - ds.startClientX) / canvasZoom;
      const dy = (lastPointerY - ds.startClientY + scrollComp) / canvasZoom;
      const deltaCols = Math.round(dx / units.colUnit);
      const deltaRows = Math.round(dy / units.rowUnit);

      let next = { ...ds.origin };
      if (ds.mode === "resize-e") {
        next.w = Math.max(1, Math.min(12 - next.x, ds.origin.w + deltaCols));
      } else if (ds.mode === "resize-s") {
        next.h = Math.max(1, ds.origin.h + deltaRows);
      } else if (ds.mode === "resize-w") {
        const maxX = ds.origin.x + ds.origin.w - 1;
        const newX = Math.max(0, Math.min(maxX, ds.origin.x + deltaCols));
        next.x = newX;
        next.w = ds.origin.w + (ds.origin.x - newX);
      } else if (ds.mode === "resize-n") {
        const maxY = ds.origin.y + ds.origin.h - 1;
        const newY = Math.max(0, Math.min(maxY, ds.origin.y + deltaRows));
        next.y = newY;
        next.h = ds.origin.h + (ds.origin.y - newY);
      } else if (ds.mode === "resize-nw") {
        const maxX = ds.origin.x + ds.origin.w - 1;
        const newX = Math.max(0, Math.min(maxX, ds.origin.x + deltaCols));
        next.x = newX;
        next.w = ds.origin.w + (ds.origin.x - newX);
        const maxY = ds.origin.y + ds.origin.h - 1;
        const newY = Math.max(0, Math.min(maxY, ds.origin.y + deltaRows));
        next.y = newY;
        next.h = ds.origin.h + (ds.origin.y - newY);
      } else {
        next.w = Math.max(1, Math.min(12 - next.x, ds.origin.w + deltaCols));
        next.h = Math.max(1, ds.origin.h + deltaRows);
      }

      if (canPlaceExcluding(ds.occupants, ds.widgetId, next)) {
        const nextPreview = {
          widgetId: ds.widgetId,
          grid: next,
          targetSurface: ds.sourceSurface
        };
        dragPreviewRef.current = nextPreview;
        setDragPreview(nextPreview);
      }
    }

    function scrollTick(now: number) {
      const dt = lastFrameTime !== null ? Math.min((now - lastFrameTime) / 1000, 0.05) : 0;
      lastFrameTime = now;

      const vh = window.innerHeight;
      let ratio = 0;
      if (lastPointerY > vh - EDGE_ZONE && resizeGrowsDown) {
        // Linear (NOT squared): predictable, controllable speed.
        ratio = Math.min(1, (lastPointerY - (vh - EDGE_ZONE)) / EDGE_ZONE);
      } else if (lastPointerY < EDGE_ZONE && resizeGrowsUp) {
        ratio = -Math.min(1, (EDGE_ZONE - lastPointerY) / EDGE_ZONE);
      }

      if (ratio !== 0 && dt > 0) {
        const before = window.scrollY;
        const maxScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
        const target = Math.max(0, Math.min(maxScroll, before + ratio * MAX_SCROLL_PX_PER_SEC * dt));
        if (target !== before) {
          window.scrollTo(0, target);
          // Re-run preview so the widget/resize follows the new scroll position.
          computePreview();
        }
      }

      rafId = requestAnimationFrame(scrollTick);
    }
    rafId = requestAnimationFrame(scrollTick);

    function onPointerMove(e: PointerEvent) {
      lastPointerX = e.clientX;
      lastPointerY = e.clientY;
      computePreview();
    }

    function onPointerUp() {
      suppressSurfaceClickUntilRef.current = Date.now() + 250;
      const latestPreview = dragPreviewRef.current;
      if (latestPreview && latestPreview.widgetId === ds.widgetId) {
        if (ds.mode === "move") {
          const targetSurface = latestPreview.targetSurface;
          const wouldCycle =
            targetSurface.kind === "tab" && ds.subtreeIds.has(targetSurface.containerId);

          if (wouldCycle) {
            // Abort — no state changes.
          } else if (sameSurface(targetSurface, ds.sourceSurface)) {
            // In-place grid update on the source surface.
            const nextGrid = findNearestVacantExcluding(
              ds.occupants,
              ds.widgetId,
              latestPreview.grid.x,
              latestPreview.grid.y,
              ds.origin.w,
              ds.origin.h
            );
            setWidgets((current) => updateWidgetGridById(current, ds.widgetId, nextGrid));
            setSaveTrigger((v) => v + 1);
          } else {
            // Cross-surface move: compute vacant slot against the target's own occupants
            // (no exclusion — the widget isn't on that surface yet).
            const targetOccupants = occupantsForSurface(targetSurface);
            const nextGrid = findNearestVacant(
              targetOccupants,
              latestPreview.grid.x,
              latestPreview.grid.y,
              ds.origin.w,
              ds.origin.h
            );
            const moving = (() => {
              const leaf = findContentWidgetById(widgets, ds.widgetId);
              if (leaf) return { ...leaf, grid: nextGrid };
              const tabsNode = findTabsWidgetById(widgets, ds.widgetId);
              if (tabsNode) return { ...tabsNode, grid: nextGrid };
              return null;
            })();
            if (moving) {
              setWidgets((current) => {
                const pruned = removeWidgetFromPage(current, ds.widgetId);
                if (targetSurface.kind === "root") {
                  return [...pruned, moving];
                }
                return appendWidgetToTab(pruned, targetSurface.containerId, targetSurface.tabId, moving);
              });
              setSaveTrigger((v) => v + 1);
            }
          }
        } else {
          setWidgets((current) => updateWidgetGridById(current, ds.widgetId, latestPreview.grid));
          setSaveTrigger((v) => v + 1);
        }
      }
      setDragState(null);
      setDragPreview(null);
      dragPreviewRef.current = null;
      setFrozenSurfaceMinHeight(null);
      if (rafId !== null) cancelAnimationFrame(rafId);
    }

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp, { once: true });
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [dragState, layout, canvasZoom]);

  useEffect(() => {
    const el = surfaceRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => { setSurfaceWidth(el.offsetWidth); });
    observer.observe(el);
    setSurfaceWidth(el.offsetWidth);
    return () => observer.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connectionLines = useMemo(() => {
    if (surfaceWidth === 0 || filters.length === 0) return [];
    const gapPx = layout.gap * canvasZoom;
    const rowHeightPx = layout.rowHeight * canvasZoom;
    const marginXPx = (layout.marginX ?? 0) * canvasZoom;
    const marginYPx = (layout.marginY ?? 0) * canvasZoom;
    const usableWidth = Math.max(1, surfaceWidth - marginXPx * 2);
    const colUnitPx = Math.max(1, (usableWidth - gapPx * 11) / 12);

    function rightCenter(grid: PageGrid) {
      const rx = marginXPx + grid.x * (colUnitPx + gapPx) + grid.w * colUnitPx + (grid.w - 1) * gapPx;
      const ry = marginYPx + grid.y * (rowHeightPx + gapPx) + (grid.h * rowHeightPx + (grid.h - 1) * gapPx) / 2;
      return { x: rx, y: ry };
    }
    function leftCenter(grid: PageGrid) {
      const lx = marginXPx + grid.x * (colUnitPx + gapPx);
      const ly = marginYPx + grid.y * (rowHeightPx + gapPx) + (grid.h * rowHeightPx + (grid.h - 1) * gapPx) / 2;
      return { x: lx, y: ly };
    }

    const lines: Array<{ key: string; fromX: number; fromY: number; toX: number; toY: number; active: boolean; label: string }> = [];
    for (const filter of filters) {
      const srcRoot = widgets.find((w) => w.id === filter.source_widget_id);
      const srcContent = findContentWidgetById(widgets, filter.source_widget_id);
      if (!srcRoot || !srcContent) continue;
      const srcGrid = previewGridFor(srcRoot.grid, srcRoot.id);
      const isActive = !!(filterSelections[filter.id]?.size);
      const srcName = srcContent.title_override || vizSourceNameByKey[vizNodeLabelKey(srcContent.source)] || "Widget";
      for (const binding of filter.bindings) {
        const tgtR = widgets.find((w) => w.id === binding.widget_id);
        const tgtContent = findContentWidgetById(widgets, binding.widget_id);
        if (!tgtR || !tgtContent) continue;
        const tgtGrid = previewGridFor(tgtR.grid, tgtR.id);
        const tgtName = tgtContent.title_override || vizSourceNameByKey[vizNodeLabelKey(tgtContent.source)] || "Widget";
        const from = rightCenter(srcGrid);
        const to = leftCenter(tgtGrid);
        lines.push({
          key: `${filter.id}::${binding.widget_id}`,
          fromX: from.x,
          fromY: from.y,
          toX: to.x,
          toY: to.y,
          active: isActive,
          label: `${srcName} (${filter.source_column}) → ${tgtName} (${binding.kind === "column" ? binding.column : `$${binding.param_name}`})`
        });
      }
    }
    return lines;
  }, [filters, widgets, filterSelections, layout, canvasZoom, surfaceWidth, vizSourceNameByKey, dragPreview]);

  const overlapError = useMemo(() => {
    function panelOverlapMessage(panel: PageWidget[], tabLabel: string): string | null {
      for (let i = 0; i < panel.length; i += 1) {
        for (let j = i + 1; j < panel.length; j += 1) {
          if (overlaps(panel[i].grid, panel[j].grid)) {
            return `In tab "${tabLabel}": widgets overlap`;
          }
        }
      }
      for (const pw of panel) {
        if (isTabsWidget(pw)) {
          for (const t of pw.tabs) {
            const inner = panelOverlapMessage(t.widgets, t.label);
            if (inner) return inner;
          }
        }
      }
      return null;
    }
    for (let i = 0; i < widgets.length; i += 1) {
      for (let j = i + 1; j < widgets.length; j += 1) {
        if (overlaps(widgets[i].grid, widgets[j].grid)) {
          const wi = widgets[i];
          const left = isTabsWidget(wi)
            ? (wi.title_override?.trim() ? wi.title_override.trim() : wi.id)
            : wi.title_override || vizSourceNameByKey[vizNodeLabelKey(wi.source)] || wi.id;
          return `Overlaps with "${left}"`;
        }
      }
    }
    for (const w of widgets) {
      if (!isTabsWidget(w)) continue;
      for (const tab of w.tabs) {
        const msg = panelOverlapMessage(tab.widgets, tab.label);
        if (msg) return msg;
      }
    }
    return null;
  }, [widgets, vizSourceNameByKey]);
  const hasActiveFilters = useMemo(
    () => hasActiveFilterSelections(filterSelections) || hasActiveDateRangeSelections(dateRangeSelections) || dynamicChartFilters.length > 0,
    [filterSelections, dateRangeSelections, dynamicChartFilters]
  );
  const dynamicColumnFilters = useMemo(
    () => dynamicChartFiltersToColumnFilters(dynamicChartFilters),
    [dynamicChartFilters]
  );

  const draftSnapshot = useMemo(
    () =>
      toDraftSnapshot({
        title,
        routeKey,
        description,
        layout,
        filters,
        widgets
      }),
    [title, routeKey, description, layout, filters, widgets]
  );

  useEffect(() => {
    latestSnapshotRef.current = draftSnapshot;
  }, [draftSnapshot]);

  function requestSave() {
    setSaveTrigger((v) => v + 1);
  }

  /**
   * ADR-0159 §6 — the escape hatch beside "Reload latest". Reloading costs
   * whatever this tab has not saved; for the rare case where that is more than
   * one gesture, the local version leaves on the clipboard.
   */
  const copyLocalPageSpec = useCallback(async () => {
    try {
      const draft = JSON.parse(latestSnapshotRef.current || "{}") as Record<string, unknown>;
      await navigator.clipboard.writeText(JSON.stringify(draft.page_spec ?? draft, null, 2));
      toast.push({ kind: "success", message: "Your version is on the clipboard." });
    } catch {
      toast.push({ kind: "error", message: "Couldn't reach the clipboard — your browser blocked it." });
    }
  }, [toast]);

  const doSaveAll = useCallback(async (snapshot: string, silent = false): Promise<boolean> => {
    if (!pageIdState) return false;
    if (overlapError) return false;
    const payload = JSON.parse(snapshot) as {
      title: string;
      route_key: string;
      description: string | null;
      page_spec: PageSpecV1;
    };
    setSaving(true);
    setSpecErrors({});
    try {
      const data = await apiFetch<{ page: PageRecord }>(`/api/tenant/pages/${encodeURIComponent(pageIdState)}`, {
        method: "PATCH",
        body: { ...payload, expected_updated_at: pageTokenRef.current }
      });
      const page = data.page;
      pageTokenRef.current = page.updated_at ?? pageTokenRef.current;
      lastSavedSnapshotRef.current = snapshot;
      if (latestSnapshotRef.current === snapshot) {
        setTitle(page.title);
        setRouteKey(page.route_key);
        setDescription(page.description ?? "");
        setLayout(page.page_spec.layout);
        setFilters(page.page_spec.filters ?? []);
        setWidgets(page.page_spec.widgets);
      }
      setStatus(page.status);
      setIsViewerLanding(page.is_viewer_landing);
      if (!silent) {
        toast.push({ kind: "success", message: "Page saved." });
      }
      return true;
    } catch (e) {
      if (e instanceof ApiRequestError && e.code === "page_spec_invalid") {
        const details = (e.data.details as Array<{ path?: string; message?: string }> | undefined) ?? [];
        const mapped: Record<string, string> = {};
        for (const entry of details) {
          if (entry.path && entry.message) mapped[entry.path] = entry.message;
        }
        setSpecErrors(mapped);
      } else if (readWriteConflict(e)) {
        // ADR-0159 §4.6 — the page moved under this tab. This MUST surface even
        // on a silent autosave: the branch below used to swallow every
        // non-validation error, which under this contract would re-send the
        // same refused write every 150 ms for as long as the tab stayed open.
        setConflict(readWriteConflict(e));
      } else {
        if (!silent) {
          toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
        }
      }
      return false;
    } finally {
      setSaving(false);
    }
  }, [pageIdState, overlapError, toast]);

  /**
   * 2026-08-24 phantom-banner fix — page spec saves are single-flight.
   *
   * The ADR-0159 token is learned only from a save's RESPONSE (`pageTokenRef`
   * below). A second save started before the first response landed carried the
   * STALE token, the hub correctly refused it, and the banner named this very
   * tab's own write from milliseconds earlier — "Brian Holmes … at <the
   * gesture's moment>". A widget resize is the canonical trigger pair: the
   * pointerdown on a resize handle blurs whatever inspector field had focus
   * (every one of them saves on blur), that save is still in flight through the
   * drag, and pointerup queues a second one behind it.
   */
  const saveFlightRef = useRef(createSingleFlight<boolean>());
  const saveAll = useCallback(
    (snapshot: string, silent = false): Promise<boolean> =>
      saveFlightRef.current.run(
        // Re-read the draft at execution time. A caller that waited was queued
        // against a snapshot that may since have moved, and writing the frozen
        // one would undo whatever the predecessor landed while it waited.
        () => doSaveAll(silent ? latestSnapshotRef.current : snapshot, silent),
        {
          skipAfterWait: () => {
            // The predecessor's refusal is now on screen. Re-sending is
            // guaranteed to be refused too, and ADR-0159 §6 pauses autosave for
            // exactly this — the queue must honour it as the effect does.
            if (conflictRef.current) return false;
            // The predecessor already wrote everything this caller carried.
            if (silent && latestSnapshotRef.current === lastSavedSnapshotRef.current) return true;
            return null;
          },
        }
      ),
    [doSaveAll]
  );

  useEffect(() => {
    if (saveTrigger === 0) return;
    if (isInitialLoadRef.current || loading || !pageIdState) return;
    if (overlapError) return;
    // ADR-0159 §6 — autosave pauses while a conflict is on screen.
    if (conflict) return;
    if (latestSnapshotRef.current === lastSavedSnapshotRef.current) return;

    if (saveTimeoutRef.current !== null) {
      window.clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = window.setTimeout(() => {
      void saveAll(latestSnapshotRef.current, true);
    }, 150);

    return () => {
      if (saveTimeoutRef.current !== null) {
        window.clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [saveTrigger, pageIdState, loading, overlapError, conflict, saveAll]);

  /**
   * ADR-0159 §6 — the staleness check that turns most conflicts into nothing.
   * Ported from the pipeline editor 2026-08-24; pages had no equivalent, so a
   * page changed by an agent while its editor sat open announced itself only
   * on the reader's next gesture — and only then as a refused save.
   *
   * Coming back to a tab is when it is most likely to be out of date and least
   * likely to have unsaved work. If the page moved while this tab sat in the
   * background: a CLEAN tab just re-reads, silently, and the reader never
   * learns there was a race. A DIRTY tab gets the banner immediately, before
   * more work is invested on a base that is already gone.
   *
   * An early warning, never a guarantee — it fires on focus, not the instant
   * the write lands. The save path still refuses a stale write regardless.
   */
  useEffect(() => {
    if (!pageId) return;
    async function checkForRemoteChange() {
      if (document.visibilityState === "hidden") return;
      if (conflictRef.current || !pageTokenRef.current) return;
      // Mid-save, this tab's own write is about to move `updated_at` itself.
      if (saveFlightRef.current.inFlight() || isInitialLoadRef.current) return;
      try {
        const data = await apiFetch<{ page: PageRecord }>(
          `/api/tenant/pages/${encodeURIComponent(pageId)}`
        );
        const remote = data.page.updated_at;
        if (!remote || remote === pageTokenRef.current) return;
        if (latestSnapshotRef.current !== lastSavedSnapshotRef.current) {
          setConflict({
            updatedAt: remote,
            actorLabel: data.page.last_write_actor_label || "Someone",
            sourceDisplay: data.page.last_write_source_display || "another surface",
            at: remote
          });
        } else {
          await load();
        }
      } catch {
        // A failed check is not worth reporting: the save path still refuses a
        // stale write, so this is an early warning, never the guarantee.
      }
    }
    function onFocus() {
      void checkForRemoteChange();
    }
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId]);

  /**
   * How many paused subscriptions are waiting on this page. Publishing is what
   * makes them deliverable, so the count belongs in the confirmation — it is
   * the consequence a person is least likely to have in mind. Best-effort: if
   * the lookup fails, the dialog simply omits the sentence rather than
   * blocking the gesture on a number.
   */
  async function pausedSubscriptionCount(): Promise<number> {
    return subscriptionCount("&paused=true");
  }

  /**
   * ADR-0154 §6.7 G-2.3 — the ACTIVE count, for the unpublish dialog. An active
   * subscription is the one that keeps firing and failing after the page goes
   * away, so it is the number that makes the warning concrete.
   */
  async function activeSubscriptionCount(): Promise<number> {
    return subscriptionCount("&paused=false");
  }

  async function subscriptionCount(filter: string): Promise<number> {
    if (!pageIdState) return 0;
    try {
      const data = await apiFetch<{ items: unknown[] }>(
        `/api/tenant/subscriptions?page_id=${encodeURIComponent(pageIdState)}${filter}`
      );
      return Array.isArray(data.items) ? data.items.length : 0;
    } catch {
      return 0;
    }
  }

  /**
   * Publishing and unpublishing are the two gestures in this editor that change
   * what other people see, and until now both were an unconfirmed dropdown
   * flick. Menus have always confirmed their equivalent; this matches that
   * precedent exactly. Archive is unchanged — it hides a page from the people
   * managing it, not from viewers.
   */
  async function confirmStatusGesture(kind: "publish" | "unpublish"): Promise<boolean> {
    const name = title || "this page";
    if (kind === "publish") {
      const paused = await pausedSubscriptionCount();
      const detail =
        paused > 0
          ? `Viewers will see this page. ${paused} paused subscription${paused === 1 ? "" : "s"} reference it.`
          : "Viewers will see this page.";
      return confirm.confirm({ title: `Publish '${name}'?`, detail, confirmLabel: "Publish" });
    }
    // ADR-0154 §6.7 G-2.3 — this used to promise that unpublishing settles the
    // page's subscriptions, which is wrong in the operative direction: they keep firing on their
    // cadence and FAIL every run, notifying nobody. The person about to
    // unpublish is exactly who needs to know that, and the same falsehood was
    // being taught at the door (one hazard, two hand-written copies, two repos).
    // The hub splices this from `ai/declaredHazards.ts`; the console cannot
    // import across repos, so the phrase is pinned by test instead.
    const active = await activeSubscriptionCount();
    const detail =
      active > 0
        ? `The page disappears from navigation. ${active} active subscription${active === 1 ? "" : "s"} will keep ` +
          `firing on schedule and fail every run until someone pauses ${active === 1 ? "it" : "them"} — unpublishing ` +
          `does not stop them.`
        : "The page disappears from navigation. Any subscriptions added later keep firing even while it is unpublished.";
    return confirm.confirm({
      title: `Unpublish '${name}'?`,
      detail,
      confirmLabel: "Unpublish"
    });
  }

  async function updateStatus(action: "publish" | "unpublish" | "archive" | "unarchive") {
    if (!pageIdState) return;
    try {
      const data = await apiFetch<{ page: PageRecord }>(`/api/tenant/pages/${encodeURIComponent(pageIdState)}/${action}`, {
        method: "POST",
        body: {}
      });
      // ADR-0159 §4.6 — these responses carry the row the write produced.
      // Discarding `updated_at` here (as this did) would leave the tab holding
      // a token the publish itself superseded, and every autosave after a
      // publish would be refused as stale.
      pageTokenRef.current = data.page.updated_at ?? pageTokenRef.current;
      setStatus(data.page.status);
      setIsViewerLanding(data.page.is_viewer_landing);
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  }

  async function toggleMain() {
    if (!pageIdState) return;
    try {
      const data = await apiFetch<{ page: PageRecord }>(`/api/tenant/pages/${encodeURIComponent(pageIdState)}/set-viewer-landing`, {
        method: "POST",
        body: { is_viewer_landing: !isViewerLanding }
      });
      pageTokenRef.current = data.page.updated_at ?? pageTokenRef.current;
      setIsViewerLanding(data.page.is_viewer_landing);
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  }

  /** Same content widget UI on the page root or inside a tab panel (shared drag/resize/actions). */
  function renderEditorContentWidget(cw: PageContentWidget) {
    const isRowFilterDriver = filters.some((f) => f.source_widget_id === cw.id && isInteractionFilter(f));
    const widgetGrid = previewGridFor(cw.grid, cw.id);
    const wKey = widgetSourceKey(cw.source);
    const resolved = resolvedItems[wKey];
    const rawRenderModel = resolved?.last?.renderModel;
    const renderModel = rawRenderModel
      ? filterRenderModel(rawRenderModel, cw.id, filters, effectiveFilterSelections, dynamicColumnFilters, dateRangeSelections)
      : undefined;
    const name =
      cw.title_override || resolved?.name || vizSourceNameByKey[vizNodeLabelKey(cw.source)] || "Widget";
    const resolveError = resolvedErrors[wKey];
    const isFocused = focusedWidgetId === cw.id;
    const isBeingDragged = dragState?.widgetId === cw.id && dragState.mode === "move";
    return (
      <section
        key={cw.id}
        className={`card pageWidgetCard${isFocused ? " widgetSelected" : ""}${isBeingDragged ? " pageWidgetDragging" : ""}`}
        style={{
          gridColumn: `${widgetGrid.x + 1} / span ${widgetGrid.w}`,
          gridRow: `${widgetGrid.y + 1} / span ${widgetGrid.h}`,
          minHeight: layout.rowHeight * widgetGrid.h * canvasZoom,
          // During a move drag, make the widget transparent to hit-testing so
          // `elementFromPoint` sees the surface underneath — critical for cross-surface drops.
          ...(isBeingDragged ? { pointerEvents: "none" as const } : {})
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <header
          className="pageWidgetHeader"
          onClick={(e) => {
            if (Date.now() < suppressSurfaceClickUntilRef.current) return;
            e.stopPropagation();
            setFocusedWidgetId(cw.id);
            setAddAnchor(null);
            setAddingInteraction(false);
          }}
        >
          <button
            className="pageWidgetDragHandle"
            type="button"
            onPointerDown={(e) => beginDrag(cw.id, "move", e)}
            onClick={(e) => e.stopPropagation()}
            aria-label="Drag widget"
          >
            Drag
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
              <h3 style={{ margin: 0 }}>{name}</h3>
              {resolved?.upstream_ai_provider ? (
                <span
                  className="pageAiBadge"
                  title={`AI-provided data.${resolved.last?.runAt ? ` As of ${new Date(resolved.last.runAt).toLocaleString()}.` : ""}`}
                >
                  ✦ AI
                </span>
              ) : null}
            </div>
            {resolved?.meta?.asOfRunAt ? (
              <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                As of: {new Date(resolved.meta.asOfRunAt).toLocaleString()}
              </div>
            ) : null}
          </div>
          <div style={{ display: "flex", gap: 4 }} onClick={(e) => e.stopPropagation()}>
            <button
              className="btn btnSm"
              type="button"
              title="Open in Operation Builder"
              aria-label="Open in Operation Builder"
              onClick={() => {
                const opId = cw.source.operation_id;
                const nodeId = cw.source.node_id;
                const returnTo = `/app/build/pages/${encodeURIComponent(pageId)}`;
                const returnLabel = encodeURIComponent(title || "Page Builder");
                nav(`/app/build/operations/${encodeURIComponent(opId)}?node=${encodeURIComponent(nodeId)}&returnTo=${encodeURIComponent(returnTo)}&returnLabel=${returnLabel}`);
              }}
            >↗</button>
            <button
              className="btn btnSm"
              type="button"
              onClick={() => duplicateWidget(cw.id)}
              aria-label="Duplicate widget"
              title="Duplicate"
            >⧉</button>
            <button
              className="btn btnSm"
              type="button"
              onClick={() => removeWidget(cw.id)}
              aria-label="Remove widget"
              title="Remove"
            >×</button>
          </div>
        </header>
        <div className="pageWidgetBody">
          {resolveError ? (
            <div className="pageWidgetMissing">
              <div>Visualization missing/unavailable</div>
              {/* Hub messages are plain sentences with display names — never ids. */}
              <div className="muted">{resolveError}</div>
            </div>
          ) : null}
          {!resolveError && !renderModel ? (
            <div className="muted">
              {resolveFailure
                ? "Widget data couldn't be loaded"
                : isPreparingItem(resolved)
                  ? "Preparing… computing this widget from cached data"
                  : resolved?.meta?.lastError
                    ? `Couldn't prepare this widget: ${resolved.meta.lastError}`
                    : resolved
                      ? "No output yet — click Refresh data to run it"
                      : "Not run yet — click Refresh data to run it"}
            </div>
          ) : null}
          {renderModel?.kind === "echarts" ? (
            <Chart
              option={resolveEchartsOption(renderModel)}
              height="100%"
              onPointClick={(point) => applyChartDynamicFilter(cw.id, point)}
            />
          ) : null}
          {renderModel?.kind === "kpi" ? <KpiCard metrics={renderModel.metrics} style={renderModel.style} /> : null}
          {renderModel?.kind === "tantable" ? (
            <TanTableViz
              model={renderModel}
              exportSourceRef={{ operationId: cw.source.operation_id, nodeId: cw.source.node_id }}
              rowFilterUnselected={rowDriveUnselectedByWidget[cw.id] === true}
              onRowFilterUnselectedChange={
                isRowFilterDriver && rawRenderModel?.kind === "tantable" && rawRenderModel.tableSpec.rowSelection === "multi"
                  ? (active) =>
                      setRowDriveUnselectedByWidget((prev) => ({
                        ...prev,
                        [cw.id]: active
                      }))
                  : undefined
              }
              isolateSelection={isolateSelectionByWidget[cw.id] === true}
              onIsolateSelectionChange={
                isRowFilterDriver
                  ? (on) =>
                      setIsolateSelectionByWidget((prev) => ({
                        ...prev,
                        [cw.id]: on
                      }))
                  : undefined
              }
              onRowSelect={isRowFilterDriver ? (ev) => handleTanTableRowSync(cw.id, ev) : undefined}
            />
          ) : null}
          {!resolveError && renderModel?.kind === "ai_content" ? <AiContentRenderer model={renderModel} /> : null}
          {!resolveError && renderModel?.kind === "ai_production_prompt" ? (
            <AiProductionPromptPanel text={renderModel.text} />
          ) : null}
          {!resolveError && resolved?.citation_sources && resolved.citation_sources.length > 0 ? (
            <div>
              <button
                type="button"
                className="pageAiSourcesBtn"
                onClick={() =>
                  setSourcesPopoverWidgetId((id) => (id === cw.id ? null : cw.id))
                }
              >
                Sources: {resolved.citation_sources.length} web references
              </button>
              {sourcesPopoverWidgetId === cw.id ? (
                <div className="pageAiSourcesPopover">
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {resolved.citation_sources.map((c, idx) => (
                      <li key={`${c.url}-${idx}`}>
                        <a href={c.url} target="_blank" rel="noopener noreferrer">
                          {c.title?.trim() ? c.title : c.url}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
        <button className="pageResizeHandle pageResizeHandleW" type="button" aria-label="Resize widget from left edge" onPointerDown={(e) => beginDrag(cw.id, "resize-w", e)} />
        <button className="pageResizeHandle pageResizeHandleN" type="button" aria-label="Resize widget from top edge" onPointerDown={(e) => beginDrag(cw.id, "resize-n", e)} />
        <button className="pageResizeHandle pageResizeHandleNW" type="button" aria-label="Resize widget from top-left corner" onPointerDown={(e) => beginDrag(cw.id, "resize-nw", e)} />
        <button className="pageResizeHandle pageResizeHandleE" type="button" aria-label="Resize widget width" onPointerDown={(e) => beginDrag(cw.id, "resize-e", e)} />
        <button className="pageResizeHandle pageResizeHandleS" type="button" aria-label="Resize widget height" onPointerDown={(e) => beginDrag(cw.id, "resize-s", e)} />
        <button className="pageResizeHandle pageResizeHandleSE" type="button" aria-label="Resize widget" onPointerDown={(e) => beginDrag(cw.id, "resize-se", e)} />
      </section>
    );
  }

  function renderEditorTabsWidget(widget: PageTabsWidget) {
    const widgetGrid = previewGridFor(widget.grid, widget.id);
    const activeTabId = editorActiveTabByContainer[widget.id] ?? widget.tabs[0]?.id ?? "";
    const activeTab = widget.tabs.find((t) => t.id === activeTabId) ?? widget.tabs[0];
    const isFocused = focusedWidgetId === widget.id;
    const isBeingDragged = dragState?.widgetId === widget.id && dragState.mode === "move";
    return (
      <section
        key={widget.id}
        className={`card pageWidgetCard pageTabsContainer${isFocused ? " widgetSelected" : ""}${isBeingDragged ? " pageWidgetDragging" : ""}`}
        style={{
          gridColumn: `${widgetGrid.x + 1} / span ${widgetGrid.w}`,
          gridRow: `${widgetGrid.y + 1} / span ${widgetGrid.h}`,
          minHeight: layout.rowHeight * widgetGrid.h * canvasZoom,
          display: "flex",
          flexDirection: "column",
          // See renderEditorContentWidget: transparent to hit-testing during move so
          // cross-surface detection can see through the widget to the surface under the cursor.
          ...(isBeingDragged ? { pointerEvents: "none" as const } : {})
        }}
        onPointerDownCapture={(e) => focusTabsWidgetFromPointerCapture(e, widget.id)}
      >
        <header
          className="pageWidgetHeader"
          onClick={(e) => {
            if (Date.now() < suppressSurfaceClickUntilRef.current) return;
            e.stopPropagation();
            setFocusedWidgetId(widget.id);
            setAddAnchor(null);
            setAddingInteraction(false);
          }}
        >
          <button
            className="pageWidgetDragHandle"
            type="button"
            onPointerDown={(e) => beginDrag(widget.id, "move", e)}
            onClick={(e) => e.stopPropagation()}
            aria-label="Drag tab container"
          >
            Drag
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 style={{ margin: 0 }}>{widget.title_override ?? ""}</h3>
          </div>
          <div style={{ display: "flex", gap: 4 }} onClick={(e) => e.stopPropagation()}>
            <button className="btn btnSm" type="button" onClick={() => removeWidget(widget.id)} aria-label="Remove" title="Remove">
              ×
            </button>
          </div>
        </header>
        <div className="tabs pageWidgetTabRow" role="tablist" aria-label="Tabbed container tabs">
          {widget.tabs.map((t) => (
            <span key={t.id} className="pageBuilderTabCell">
              <button
                type="button"
                role="tab"
                aria-selected={activeTab?.id === t.id}
                className={`tab${activeTab?.id === t.id ? " tabActive" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setEditorActiveTabByContainer((prev) => ({ ...prev, [widget.id]: t.id }));
                }}
              >
                {t.label}
              </button>
              {widget.tabs.length > 1 ? (
                <button
                  type="button"
                  className="pageBuilderTabClose"
                  aria-label={`Remove tab ${t.label}`}
                  title="Remove tab"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeTabFromContainer(widget.id, t.id);
                  }}
                >
                  ×
                </button>
              ) : null}
            </span>
          ))}
        </div>
        <div
          className="pageBuilderNestedSurface"
          data-pb-surface="tab"
          data-pb-container-id={widget.id}
          data-pb-tab-id={activeTab?.id ?? widget.tabs[0]!.id}
          style={{
            flex: 1,
            minHeight: 120,
            display: "grid",
            gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
            gridAutoRows: `${layout.rowHeight * canvasZoom}px`,
            gap: layout.gap * canvasZoom,
            padding: layout.gap * canvasZoom,
            overflow: "auto"
          }}
          onDoubleClick={(e) =>
            onNestedTabSurfaceDoubleClick(e, widget.id, activeTab?.id ?? widget.tabs[0]!.id)
          }
        >
          {activeTab?.widgets.map((panelItem) => renderEditorPanelItem(panelItem))}
          {(() => {
            if (!dragPreview || !dragState) return null;
            if (dragState.mode !== "move") return null;
            if (sameSurface(dragPreview.targetSurface, dragState.sourceSurface)) return null;
            if (dragPreview.targetSurface.kind !== "tab") return null;
            if (dragPreview.targetSurface.containerId !== widget.id) return null;
            if (dragPreview.targetSurface.tabId !== (activeTab?.id ?? widget.tabs[0]!.id)) return null;
            const g = dragPreview.grid;
            return (
              <div
                className="pageDragDropGhost"
                style={{
                  gridColumn: `${g.x + 1} / span ${g.w}`,
                  gridRow: `${g.y + 1} / span ${g.h}`,
                  pointerEvents: "none"
                }}
              />
            );
          })()}
        </div>
        <button
          className="pageResizeHandle pageResizeHandleW"
          type="button"
          aria-label="Resize tab container from left edge"
          onPointerDown={(e) => beginDrag(widget.id, "resize-w", e)}
        />
        <button
          className="pageResizeHandle pageResizeHandleN"
          type="button"
          aria-label="Resize tab container from top edge"
          onPointerDown={(e) => beginDrag(widget.id, "resize-n", e)}
        />
        <button
          className="pageResizeHandle pageResizeHandleNW"
          type="button"
          aria-label="Resize tab container from top-left corner"
          onPointerDown={(e) => beginDrag(widget.id, "resize-nw", e)}
        />
        <button
          className="pageResizeHandle pageResizeHandleE"
          type="button"
          aria-label="Resize width"
          onPointerDown={(e) => beginDrag(widget.id, "resize-e", e)}
        />
        <button
          className="pageResizeHandle pageResizeHandleS"
          type="button"
          aria-label="Resize height"
          onPointerDown={(e) => beginDrag(widget.id, "resize-s", e)}
        />
        <button
          className="pageResizeHandle pageResizeHandleSE"
          type="button"
          aria-label="Resize"
          onPointerDown={(e) => beginDrag(widget.id, "resize-se", e)}
        />
      </section>
    );
  }

  function renderEditorPanelItem(item: PageWidget) {
    if (isTabsWidget(item)) {
      return renderEditorTabsWidget(item);
    }
    return renderEditorContentWidget(item);
  }

  if (loading) {
    return (
      <PageLayout title="Page Builder">
        <div className="card">
          <LoadingState />
        </div>
      </PageLayout>
    );
  }
  if (error) {
    return (
      <PageLayout title="Page Builder">
        <div className="card">
          <ErrorState detail={error} />
        </div>
      </PageLayout>
    );
  }

  const drivesFilters = filters.filter((f) => f.source_widget_id === focusedWidgetId);
  const drivenByFilters = focusedWidgetId
    ? filters.filter((f) => f.bindings.some((b) => b.widget_id === focusedWidgetId))
    : [];
  const focusedRm = inspectorContent
    ? resolvedItems[widgetSourceKey(inspectorContent.source)]?.last?.renderModel
    : undefined;
  const focusedBindableTarget = inspectorContent
    ? bindableSources
        .find((s) => s.operation_id === inspectorContent.source.operation_id)
        ?.targets.find(
          (t) =>
            t.node_id === inspectorContent.source.node_id &&
            resolvedSourcePort(inspectorContent.source, resolvedItems[widgetSourceKey(inspectorContent.source)]) === t.port_id
        )
    : undefined;
  // rowSelection from saved op config takes priority; render model is the fallback
  const effectiveRowSelection =
    (focusedBindableTarget?.row_selection as "none" | "single" | "multi" | undefined) ??
    (focusedRm?.kind === "tantable" ? focusedRm.tableSpec?.rowSelection : undefined) ??
    "none";
  const needsRowSelectionWarning =
    drivesFilters.length > 0 &&
    focusedRm?.kind === "tantable" &&
    (effectiveRowSelection === "none" || !effectiveRowSelection);
  const srcColsForFocused = focusedWidgetId ? columnsForWidget(focusedWidgetId) : [];
  const tgtColsForInteraction = newInteractionTarget ? columnsForWidget(newInteractionTarget) : [];

  return (
    <PageLayout
      title={
        <span className="breadcrumbTitle">
          <button className="breadcrumbLink" type="button" onClick={() => nav("/app/build/pages")}>
            Page Builder
          </button>
          <span className="breadcrumbSep">/</span>
          <span>{title || "Page"}</span>
          {saving ? <span className="muted" style={{ marginLeft: 8, fontSize: 14 }}>Saving...</span> : null}
        </span>
      }
      actions={
        <>
          <button
            className="btn"
            type="button"
            onClick={() => void refreshData()}
            disabled={refreshing || !pageIdState}
            title="Run every widget's pipeline with a fresh upstream pull, then reload the page's data. Runs the saved page — wait for autosave to finish first."
          >
            {refreshing ? "Refreshing..." : "Refresh data"}
          </button>
          <button className="btn" type="button" onClick={() => nav(`/app/pages/${encodeURIComponent(routeKey)}`)}>Open runtime</button>
        </>
      }
    >
      {conflict && (
        <WriteConflictBanner
          noun="page"
          actorLabel={conflict.actorLabel}
          sourceDisplay={conflict.sourceDisplay}
          at={conflict.at}
          busy={saving}
          onReload={() => void load()}
          onCopy={() => void copyLocalPageSpec()}
        />
      )}
      {resolveFailure && (
        <div className="pageWidgetMissing" role="alert" style={{ marginBottom: 8, display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ flex: 1 }}>Couldn't load widget data: {resolveFailure}</span>
          <button className="btn btnSm" type="button" onClick={() => void resolveWidgets()}>Retry</button>
        </div>
      )}
      {/* ADR-0068 scope header — scope pill + Change scope + Clone */}
      {pageIdState && (
        <div style={{ marginBottom: 8 }}>
          <ScopeHeader
            entityKind="page"
            entityId={pageIdState}
            scope={pageScope}
            ownerGroupId={pageOwnerGroupId}
            ownerGroupName={pageOwnerGroupName}
            ownerAccountId={pageOwnerAccountId}
            callerAccountId={callerAccountId}
            callerRole={callerRole}
            callerGroupIds={callerGroups.map((g) => g.id)}
            onScopeChanged={() => void load()}
            cloneEditorPathPrefix="/app/build/pages"
          />
        </div>
      )}
      <div className="pageBuilderLayout">

        {/* ── CANVAS ── */}
        <div className="pageBuilderCanvas" ref={pageInspectorAreaRef}>
          {filters.some(isPageSlicerFilter) || dynamicChartFilters.length > 0 ? (
            <div className="pageFilterBar" style={{ marginBottom: 8 }}>
              {filters.filter(isPageSlicerFilter).map((filter) => {
                if (isDateRangeSlicer(filter)) {
                  const drState = dateRangeSelections[filter.id] ?? { start: filter.default_start ?? null, end: filter.default_end ?? null };
                  return (
                    <PageFilterDateRange
                      key={filter.id}
                      filter={filter}
                      state={drState}
                      onChange={(state) => setDateRangeSelections((prev) => nextSetDateRange(prev, filter.id, state))}
                      onClear={() => setDateRangeSelections((prev) => nextClearDateRange(prev, filter.id))}
                    />
                  );
                }
                const srcKey = filterSourceKey(filter, widgets);
                const srcRm = srcKey ? resolvedItems[srcKey]?.last?.renderModel : undefined;
                const options = srcRm ? distinctValues(srcRm, filter.source_column) : [];
                const selected = filterSelections[filter.id] ?? new Set<string>();
                return (
                  <PageFilterDropdown
                    key={filter.id}
                    filter={filter}
                    options={options}
                    selected={selected}
                    onToggle={(val) => toggleFilterValue(filter.id, val, filter.multi)}
                    onSelectAll={() => selectAllFilterValues(filter.id, options)}
                    onClear={() => clearFilterSelection(filter.id)}
                  />
                );
              })}
              {hasActiveFilters ? (
                <button className="btn pageFilterClearAll" type="button" onClick={clearAllFilterSelections}>Clear all</button>
              ) : null}
              <PageActiveFilterChips
                chips={dynamicChartFilters}
                onRemove={(id) => setDynamicChartFilters((prev) => nextRemoveDynamicChartFilter(prev, id))}
              />
            </div>
          ) : null}

          <div className="pageDesignTopbar">
            {overlapError ? <div className="muted" style={{ marginBottom: 8, fontSize: 13 }}>{overlapError}</div> : null}
            <div className="pageZoomControls" role="group" aria-label="Canvas zoom controls">
              <button
                className="btn btnSm"
                type="button"
                onClick={() => setCanvasZoom((current) => clampCanvasZoom(current - CANVAS_ZOOM_STEP))}
                disabled={canvasZoom <= MIN_CANVAS_ZOOM}
                aria-label="Zoom out"
              >-</button>
              <button className="btn btnSm pageZoomValue" type="button" onClick={() => setCanvasZoom(1)} aria-label="Reset zoom to 100%">
                {zoomPercent}%
              </button>
              <button
                className="btn btnSm"
                type="button"
                onClick={() => setCanvasZoom((current) => clampCanvasZoom(current + CANVAS_ZOOM_STEP))}
                disabled={canvasZoom >= MAX_CANVAS_ZOOM}
                aria-label="Zoom in"
              >+</button>
            </div>
          </div>

          <div className="pageBuilderCanvasCard">
            <div className="pageDesignViewport">
              <div className="pageDesignZoomLayer" style={{ width: `${zoomPercent}%` }}>
                <div
                  ref={surfaceRef}
                  className="pageDesignSurface"
                  data-pb-surface="root"
                  onClick={onSurfaceClick}
                  onDoubleClick={onSurfaceDoubleClick}
                  style={{
                    gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
                    gridAutoRows: `${layout.rowHeight * canvasZoom}px`,
                    gap: layout.gap * canvasZoom,
                    minHeight:
                      frozenSurfaceMinHeight !== null
                        ? `${frozenSurfaceMinHeight}px`
                        : `max(50vh, 400px)`,
                    paddingLeft: (layout.marginX ?? 0) * canvasZoom,
                    paddingRight: (layout.marginX ?? 0) * canvasZoom,
                    paddingTop: (layout.marginY ?? 0) * canvasZoom,
                    paddingBottom: (layout.marginY ?? 0) * canvasZoom
                  }}
                >
                  {widgets.map((widget) => {
                    if (isTabsWidget(widget)) {
                      return renderEditorTabsWidget(widget);
                    }
                    return renderEditorContentWidget(widget);
                  })}

                  {/* Cross-surface drop ghost: shows where a widget dragged from a tab panel
                      will land on the root surface. */}
                  {dragPreview && dragState && dragState.mode === "move"
                    && !sameSurface(dragPreview.targetSurface, dragState.sourceSurface)
                    && dragPreview.targetSurface.kind === "root" ? (
                    <div
                      className="pageDragDropGhost"
                      style={{
                        gridColumn: `${dragPreview.grid.x + 1} / span ${dragPreview.grid.w}`,
                        gridRow: `${dragPreview.grid.y + 1} / span ${dragPreview.grid.h}`,
                        pointerEvents: "none"
                      }}
                    />
                  ) : null}

                  {/* Empty canvas hint */}
                  {widgets.length === 0 ? (
                    <div className="canvasEmptyHint" style={{ pointerEvents: "none" }}>
                      Double-click anywhere to add a widget
                    </div>
                  ) : null}

                  {/* connection lines SVG overlay — rendered last so lines appear on top */}
                  {connectionLines.length > 0 && !dragState ? (
                    <svg
                      className="connectionLinesOverlay"
                      style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "visible", width: "100%", height: "100%" }}
                    >
                      <defs>
                        <marker id="cxArrow" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                          <path d="M0,0 L0,6 L8,3 z" fill="var(--accent-muted,#6366f1)" />
                        </marker>
                        <marker id="cxArrowActive" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                          <path d="M0,0 L0,6 L8,3 z" fill="var(--accent,#4f46e5)" />
                        </marker>
                      </defs>
                      {connectionLines.map((line) => {
                        const cx = Math.max(60, Math.abs(line.toX - line.fromX) * 0.4);
                        const d = `M${line.fromX},${line.fromY} C${line.fromX + cx},${line.fromY} ${line.toX - cx},${line.toY} ${line.toX},${line.toY}`;
                        return (
                          <path
                            key={line.key}
                            d={d}
                            stroke={line.active ? "var(--accent,#4f46e5)" : "var(--accent-muted,#6366f1)"}
                            strokeWidth="1.5"
                            strokeDasharray={line.active ? undefined : "4 3"}
                            fill="none"
                            markerEnd={line.active ? "url(#cxArrowActive)" : "url(#cxArrow)"}
                            opacity={line.active ? 1 : 0.6}
                          >
                            <title>{line.label}</title>
                          </path>
                        );
                      })}
                    </svg>
                  ) : null}

                  {/* Floating "Add widget" prompt — appears at the click location */}
                  {addAnchor ? (
                    <div
                      className="pageAddWidgetPrompt isFixed"
                      style={{ left: addAnchor.left, top: addAnchor.top }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        className="btn btnPrimary"
                        type="button"
                        onClick={() => addWidgetAt(addAnchor.x, addAnchor.y, addAnchor.tabTarget ?? null)}
                      >
                        Add widget
                      </button>
                      <button
                        className="btn"
                        type="button"
                        onClick={() => addTabbedContainerAt(addAnchor.x, addAnchor.y, addAnchor.tabTarget ?? null)}
                      >
                        Tabbed container
                      </button>
                      <button className="btn" type="button" onClick={() => setAddAnchor(null)}>
                        Cancel
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── INSPECTOR (ADR-0129 overlay flyout) ── */}
        <InspectorFlyout
          surfaceId="pageBuilder"
          panelId="inspector"
          open={!inspectorCollapsed}
          onOpenChange={(o) => setInspectorCollapsed(!o)}
          stripLabel="Inspector"
          topOffset={pageInspectorFlyoutTop}
          widthConfig={{ min: 280, max: (vw) => Math.min(vw - 80, 720), default: 340 }}
          headerNav={focusedWidgetId ? (inspectorTabs ? "Tab group" : "Widget") : "Page"}
        >
        <div className="pageBuilderInspector" style={{ flex: 1, minHeight: 0, minWidth: 0, width: "auto", maxWidth: "none" }}>
          {inspectorSelection ? (
            <>
              <div className="inspectorHeader">
                <div className="inspectorTabs">
                  {(inspectorTabs
                    ? (["settings"] as const)
                    : (["settings", "interactions", "edit"] as const)
                  ).map((t) => (
                    <button key={t} type="button" className={`inspectorTab${inspectorTab === t ? " inspectorTabActive" : ""}`} onClick={() => setInspectorTab(t)}>
                      {t === "settings" ? "Settings" : t === "interactions" ? "Interactions" : "Edit"}
                    </button>
                  ))}
                </div>
              </div>

              {inspectorTabs && inspectorTab === "settings" ? (
                <div className="inspectorTabPanel">
                  <div className="field">
                    <label className="label">Container title</label>
                    <input
                      className="input"
                      value={inspectorTabs.title_override ?? ""}
                      onChange={(e) => setTabsPatch(inspectorTabs.id, { title_override: e.target.value || undefined })}
                      onBlur={() => requestSave()}
                    />
                  </div>
                  <div className="field">
                    <label className="label">Position &amp; dimensions (root grid)</label>
                    <div className="widgetGridInputs">
                      {(["x", "y", "w", "h"] as const).map((key) => (
                        <div key={key} className="widgetGridField">
                          <span className="widgetGridLabel">{key.toUpperCase()}</span>
                          <input
                            className="input widgetGridInput"
                            type="number"
                            value={inspectorTabs.grid[key]}
                            onChange={(e) => setTabsGridPatch(inspectorTabs.id, { [key]: Number(e.target.value) })}
                            onBlur={() => requestSave()}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="field">
                    <label className="label">Tab labels</label>
                    {inspectorTabs.tabs.map((t, idx) => (
                      <input
                        key={t.id}
                        className="input"
                        style={{ marginBottom: 6 }}
                        value={t.label}
                        onChange={(e) => {
                          const v = e.target.value;
                          setWidgets((c) =>
                            updateTabsWidgetById(c, inspectorTabs.id, (tw) => ({
                              ...tw,
                              tabs: tw.tabs.map((x, i) => (i === idx ? { ...x, label: v } : x))
                            }))
                          );
                        }}
                        onBlur={() => requestSave()}
                      />
                    ))}
                    <button
                      className="btn btnSm"
                      type="button"
                      style={{ marginTop: 8 }}
                      onClick={() => {
                        const nid = typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `tab_${Date.now()}`;
                        setWidgets((c) =>
                          updateTabsWidgetById(c, inspectorTabs.id, (tw) => ({
                            ...tw,
                            tabs: [...tw.tabs, { id: nid, label: `Tab ${tw.tabs.length + 1}`, widgets: [] }]
                          }))
                        );
                        requestSave();
                      }}
                    >
                      Add tab
                    </button>
                  </div>
                  <button className="btn widgetRemoveBtn" type="button" style={{ marginTop: 16 }} onClick={() => removeWidget(inspectorTabs.id)}>
                    Remove tab container
                  </button>
                </div>
              ) : null}

              {inspectorContent && inspectorTab === "settings" ? (
                <div className="inspectorTabPanel">
                  <div className="field">
                    <label className="label">Data source</label>
                    <div className="muted" style={{ fontSize: 13, marginBottom: 8 }}>
                      {vizSourceNameByKey[vizNodeLabelKey(inspectorContent.source)] ||
                        `${bindableSources.find((s) => s.operation_id === inspectorContent.source.operation_id)?.operation_name ?? inspectorContent.source.operation_id} / ${inspectorContent.source.node_id}`}
                      {(inspectorContent.source.port_id?.trim() && inspectorContent.source.port_id !== "tabular_out") ? ` / ${inspectorContent.source.port_id}` : ""}
                    </div>
                    <button
                      className="btn btnSm"
                      type="button"
                      onClick={() => {
                        setSourcePickerChangeWidgetId(inspectorContent.id);
                        setSourcePickerInitial(inspectorContent.source);
                        setPendingRootGrid(null);
                        setPendingTabTarget(null);
                        setSourcePickerOpen(true);
                      }}
                    >
                      Change…
                    </button>
                  </div>

                  <div className="field">
                    <label className="label">Title override</label>
                    <input
                      className="input"
                      placeholder="Leave blank to use default"
                      value={inspectorContent.title_override ?? ""}
                      onChange={(e) => setContentPatch(inspectorContent.id, { title_override: e.target.value || undefined })}
                      onBlur={() => requestSave()}
                    />
                  </div>

                  <div className="field">
                    <label className="label">Size</label>
                    <div className="widgetSizePresets">
                      {([{ label: "Full", w: 12 }, { label: "Half", w: 6 }, { label: "Third", w: 4 }, { label: "Quarter", w: 3 }] as const).map((preset) => (
                        <button
                          key={preset.label}
                          className={`widgetSizePresetBtn${inspectorContent.grid.w === preset.w ? " active" : ""}`}
                          type="button"
                          onClick={() => {
                            setContentGridPatch(inspectorContent.id, { x: 0, w: preset.w });
                            requestSave();
                          }}
                        >{preset.label}</button>
                      ))}
                    </div>
                  </div>

                  <div className="field">
                    <label className="label">Position &amp; dimensions</label>
                    <div className="widgetGridInputs">
                      {(["x", "y", "w", "h"] as const).map((key) => (
                        <div key={key} className="widgetGridField">
                          <span className="widgetGridLabel">{key.toUpperCase()}</span>
                          <input
                            className="input widgetGridInput"
                            type="number"
                            value={inspectorContent.grid[key]}
                            onChange={(e) => setContentGridPatch(inspectorContent.id, { [key]: Number(e.target.value) })}
                            onBlur={() => requestSave()}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <button className="btn widgetRemoveBtn" type="button" style={{ marginTop: 16 }} onClick={() => removeWidget(inspectorContent.id)}>
                    Remove widget
                  </button>
                </div>
              ) : null}

              {/* ── WIDGET TAB: Interactions (content only) ── */}
              {inspectorContent && inspectorTab === "interactions" ? (
                <div className="inspectorTabPanel">
                  {needsRowSelectionWarning ? (
                    <div className="interactionWarning">
                      Row selection is not enabled on this widget's visualization. Open the{" "}
                      <a href={`/app/build/operations/${encodeURIComponent(inspectorContent.source.operation_id)}`} target="_blank" rel="noreferrer">
                        operation editor
                      </a>{" "}
                      and set <code>rowSelection</code> to <code>single</code> or <code>multi</code> in the viz node.
                    </div>
                  ) : null}

                  {/* Filters this widget applies to others */}
                  <div className="inspectorSubLabel" style={{ marginBottom: 6 }}>
                    When a row is selected here, filter:
                  </div>

                  {drivesFilters.length > 0
                    ? drivesFilters.map((f) =>
                        f.bindings.map((b) => {
                          const tgt = findContentWidgetById(widgets, b.widget_id);
                          const tgtName = tgt ? (tgt.title_override || vizSourceNameByKey[vizNodeLabelKey(tgt.source)] || "Widget") : b.widget_id;
                          return (
                            <div key={`${f.id}::${b.widget_id}`} className="interactionRow">
                              <span className="interactionRowLabel">
                                <button type="button" className="btn btnLink" onClick={() => { setFocusedWidgetId(b.widget_id); setAddingInteraction(false); }}>
                                  {tgtName}
                                </button>
                                <span className="muted"> where </span>
                                <span className="interactionCol">{b.kind === "column" ? b.column : `$${b.param_name}`}</span>
                                <span className="muted"> = </span>
                                <span className="interactionCol">{f.source_column}</span>
                              </span>
                              <button type="button" className="btn btnSm" title="Remove filter" onClick={() => { removeInteraction(f.id, b.widget_id); requestSave(); }}>×</button>
                            </div>
                          );
                        })
                      )
                    : !addingInteraction
                      ? <div className="muted" style={{ fontSize: 13, marginBottom: 8 }}>No filters configured.</div>
                      : null
                  }

                  {/* Add filter form */}
                  {addingInteraction ? (
                    <div className="addInteractionForm">
                      <div className="interactionSentence">
                        <span>Filter</span>
                        <select
                          className="input interactionSelect"
                          value={newInteractionTarget}
                          onChange={(e) => { setNewInteractionTarget(e.target.value); setNewInteractionSourceCol(""); setNewInteractionTargetCol(""); }}
                        >
                          <option value="">choose widget…</option>
                          {flattenContentWidgets(widgets)
                            .filter((w) => w.id !== focusedWidgetId)
                            .map((w) => {
                              const labelKey = vizNodeLabelKey(w.source);
                              return <option key={w.id} value={w.id}>{w.title_override || vizSourceNameByKey[labelKey] || w.id}</option>;
                            })}
                        </select>
                        <span>where</span>
                        <select
                          className="input interactionSelect"
                          value={newInteractionTargetCol}
                          onChange={(e) => setNewInteractionTargetCol(e.target.value)}
                          disabled={!newInteractionTarget}
                        >
                          <option value="">{!newInteractionTarget ? "column…" : tgtColsForInteraction.length === 0 ? "not run yet" : "column…"}</option>
                          {tgtColsForInteraction.map((col) => <option key={col} value={col}>{col}</option>)}
                        </select>
                        <span>=</span>
                        <select
                          className="input interactionSelect"
                          value={newInteractionSourceCol}
                          onChange={(e) => setNewInteractionSourceCol(e.target.value)}
                        >
                          <option value="">{srcColsForFocused.length === 0 ? "not run yet" : "column…"}</option>
                          {srcColsForFocused.map((col) => <option key={col} value={col}>{col}</option>)}
                        </select>
                        <span className="muted" style={{ fontSize: 12 }}>(this widget)</span>
                      </div>
                      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                        <button
                          className="btn btnPrimary btnSm"
                          type="button"
                          disabled={!newInteractionTarget || !newInteractionSourceCol || !newInteractionTargetCol}
                          onClick={() => {
                            addInteraction(focusedWidgetId!, newInteractionTarget, newInteractionSourceCol, newInteractionTargetCol);
                            setAddingInteraction(false);
                            setNewInteractionTarget(""); setNewInteractionSourceCol(""); setNewInteractionTargetCol("");
                            requestSave();
                          }}
                        >Save filter</button>
                        <button className="btn btnSm" type="button" onClick={() => { setAddingInteraction(false); setNewInteractionTarget(""); setNewInteractionSourceCol(""); setNewInteractionTargetCol(""); }}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <button
                      className="btn btnSm"
                      type="button"
                      disabled={flattenContentWidgets(widgets).length < 2}
                      onClick={() => setAddingInteraction(true)}
                    >
                      + Add filter
                    </button>
                  )}

                  {/* Filters applied to this widget by others */}
                  {drivenByFilters.length > 0 ? (
                    <div style={{ marginTop: 20 }}>
                      <div className="inspectorSubLabel" style={{ marginBottom: 6 }}>This widget is filtered by:</div>
                      {drivenByFilters.map((f) => {
                        let srcName: string;
                        if (f.source_viz) {
                          srcName = vizSourceNameByKey[vizNodeLabelKey(f.source_viz)] || `${f.source_viz.operation_id} / ${f.source_viz.node_id}`;
                        } else {
                          const src = findContentWidgetById(widgets, f.source_widget_id);
                          srcName = src ? (src.title_override || vizSourceNameByKey[vizNodeLabelKey(src.source)] || "Widget") : f.source_widget_id;
                        }
                        const myBinding = f.bindings.find((b) => b.widget_id === inspectorContent.id);
                        return (
                          <div key={f.id} className="interactionRow interactionRowClickable" onClick={() => { setFocusedWidgetId(f.source_widget_id); setAddingInteraction(false); }}>
                            <span className="interactionRowLabel">
                              <button type="button" className="btn btnLink">{srcName}</button>
                              {myBinding && (
                                <>
                                  <span className="muted"> on </span>
                                  <span className="interactionCol">{f.source_column}</span>
                                  <span className="muted"> → </span>
                                  <span className="interactionCol">{myBinding.kind === "column" ? myBinding.column : `$${myBinding.param_name}`}</span>
                                </>
                              )}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              ) : null}

              {/* ── WIDGET TAB: Edit (deep-link to operation) ── */}
              {inspectorContent && inspectorTab === "edit" ? (
                <div className="inspectorTabPanel">
                  {(() => {
                    const opId = inspectorContent.source.operation_id;
                    const nodeId = inspectorContent.source.node_id;
                    const returnTo = `/app/build/pages/${encodeURIComponent(pageId)}`;
                    const returnLabel = encodeURIComponent(title || "Page Builder");
                    const opUrl = `/app/build/operations/${encodeURIComponent(opId)}?node=${encodeURIComponent(nodeId)}&returnTo=${encodeURIComponent(returnTo)}&returnLabel=${returnLabel}`;
                    return (
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        <div className="muted" style={{ fontSize: 13, lineHeight: 1.4 }}>
                          Open this widget's operation to edit its visualization, transform, or data source. A link back to this page will appear in the operation editor.
                        </div>
                        <a
                          className="btn btnPrimary"
                          href={opUrl}
                          onClick={(e) => { e.preventDefault(); nav(opUrl); }}
                          style={{ textAlign: "center", textDecoration: "none" }}
                        >
                          Open in Operation Builder →
                        </a>
                      </div>
                    );
                  })()}
                </div>
              ) : null}

            </>
          ) : (
            <>
              {/* page context: tabs */}
              <div className="inspectorHeader">
                <div className="inspectorTabs">
                  {(["page", "slicers", "ai"] as const).map((t) => (
                    <button key={t} type="button" className={`inspectorTab${inspectorTab === t ? " inspectorTabActive" : ""}`} onClick={() => setInspectorTab(t)}>
                      {t === "page"
                        ? "Page"
                        : t === "slicers"
                          ? `Slicers${filters.some(isPageSlicerFilter) ? ` (${filters.filter(isPageSlicerFilter).length})` : ""}`
                          : "AI"}
                    </button>
                  ))}
                </div>
              </div>

              {/* ── PAGE TAB: Page ── */}
              {inspectorTab === "page" ? (
                <div className="inspectorTabPanel">
                  <div className="field">
                    <label className="label">Title</label>
                    <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} onBlur={() => requestSave()} />
                  </div>
                  <div className="field">
                    <label className="label">Route key</label>
                    <input className="input" value={routeKey} onChange={(e) => setRouteKey(e.target.value)} onBlur={() => requestSave()} placeholder="e.g. my-dashboard" />
                    <span className="help">URL slug: 3–64 chars, lowercase a–z, 0–9, hyphens</span>
                  </div>
                  <div className="field">
                    <label className="label">Status</label>
                    <select
                      className="input"
                      value={status}
                      onChange={(e) => {
                        const next = e.target.value as PageStatus;
                        if (next === status) return;
                        void (async () => {
                          // Archived → Active is one gesture to the person
                          // making it, so it confirms once, as a publish — the
                          // unarchive is plumbing on the way there.
                          if (next === "published" && (status === "draft" || status === "archived")) {
                            if (!(await confirmStatusGesture("publish"))) return;
                            if (status === "archived") await updateStatus("unarchive");
                            await updateStatus("publish");
                          } else if (next === "draft" && status === "published") {
                            if (!(await confirmStatusGesture("unpublish"))) return;
                            await updateStatus("unpublish");
                          } else if (next === "draft" && status === "archived") {
                            await updateStatus("unarchive");
                          } else if (next === "archived") {
                            await updateStatus("archive");
                          }
                        })();
                      }}
                    >
                      <option value="draft">Draft</option>
                      <option value="published">Active</option>
                      <option value="archived">Archived</option>
                    </select>
                  </div>
                  <div className="settingsCheckboxField">
                    <input type="checkbox" checked={isViewerLanding} onChange={() => { if (status === "published") void toggleMain(); }} disabled={status !== "published"} style={{ cursor: status === "published" ? "pointer" : "not-allowed" }} />
                    <span className="label" style={{ fontWeight: 500, color: "var(--text)" }}>Main page</span>
                  </div>
                  <div className="field">
                    <label className="label">Description</label>
                    <textarea className="input reportTextarea" value={description} onChange={(e) => setDescription(e.target.value)} onBlur={() => requestSave()} />
                  </div>
                  {Object.entries(specErrors).length > 0 ? (
                    <div style={{ marginTop: 12 }}>
                      {Object.entries(specErrors).map(([path, msg]) => (
                        <div key={path} className="muted" style={{ fontSize: 12 }}>{path}: {msg}</div>
                      ))}
                    </div>
                  ) : null}

                  <div className="inspectorSectionDivider" />

                  <div className="inspectorSubLabel" style={{ marginBottom: 8 }}>Layout</div>
                  <div className="layoutGridInputs">
                    <div className="widgetGridField">
                      <span className="widgetGridLabel">Row H</span>
                      <input className="input widgetGridInput" type="number" value={layout.rowHeight} onChange={(e) => setLayout((v) => ({ ...v, rowHeight: Number(e.target.value) }))} onBlur={() => requestSave()} />
                    </div>
                    <div className="widgetGridField">
                      <span className="widgetGridLabel">Gap</span>
                      <input className="input widgetGridInput" type="number" value={layout.gap} onChange={(e) => setLayout((v) => ({ ...v, gap: Number(e.target.value) }))} onBlur={() => requestSave()} />
                    </div>
                    {layout.marginX !== undefined && (
                      <div className="widgetGridField">
                        <span className="widgetGridLabel">Margin X</span>
                        <input className="input widgetGridInput" type="number" value={layout.marginX} onChange={(e) => setLayout((v) => ({ ...v, marginX: Number(e.target.value) }))} onBlur={() => requestSave()} />
                      </div>
                    )}
                    {layout.marginY !== undefined && (
                      <div className="widgetGridField">
                        <span className="widgetGridLabel">Margin Y</span>
                        <input className="input widgetGridInput" type="number" value={layout.marginY} onChange={(e) => setLayout((v) => ({ ...v, marginY: Number(e.target.value) }))} onBlur={() => requestSave()} />
                      </div>
                    )}
                  </div>
                </div>
              ) : null}

              {/* ── PAGE TAB: Slicers ── */}
              {inspectorTab === "slicers" ? (
                <div className="inspectorTabPanel">
                  <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
                    <button className="btn btnSm" type="button" onClick={() => { addFilter(); requestSave(); }}>+ Add slicer</button>
                  </div>
                  {!filters.some(isPageSlicerFilter) ? (
                    <div className="slicerEmptyState">No slicers configured.</div>
                  ) : null}
                  {filters.filter(isPageSlicerFilter).map((filter, filterIdx) => {
                    const isDR = isDateRangeSlicer(filter);
                    const srcCols = columnsForFilter(filter);
                    // Current slicer source — encoded as "widget:<id>" or "viz:<sourceKey>" for the select value
                    const currentSelectValue = filter.source_viz
                      ? `viz:${widgetSourceKey(filter.source_viz)}`
                      : filter.source_widget_id
                        ? `widget:${filter.source_widget_id}`
                        : "";
                    return (
                      <div key={filter.id} className="slicerConfigCard">
                        <div className="slicerConfigHeader">
                          <span className="slicerConfigNum">Slicer {filterIdx + 1}</span>
                          <button className="btn slicerConfigRemove" type="button" onClick={() => removeFilter(filter.id)} aria-label="Remove slicer">&times;</button>
                        </div>
                        <div className="slicerConfigBody">
                          {/* Type selector */}
                          <div className="slicerConfigRow">
                            <div className="field">
                              <label className="label">Type</label>
                              <select
                                className="input"
                                value={isDR ? "date_range" : "list"}
                                onChange={(e) => {
                                  const newType = e.target.value as "list" | "date_range";
                                  if (newType === "date_range") {
                                    updateFilter(filter.id, {
                                      slicer_type: "date_range",
                                      source_widget_id: "",
                                      source_viz: undefined,
                                      source_column: "",
                                      bindings: []
                                    });
                                  } else {
                                    updateFilter(filter.id, {
                                      slicer_type: undefined,
                                      default_start: undefined,
                                      default_end: undefined,
                                      bindings: []
                                    });
                                  }
                                  requestSave();
                                }}
                              >
                                <option value="list">List</option>
                                <option value="date_range">Date Range</option>
                              </select>
                            </div>
                          </div>

                          {/* Label row */}
                          <div className="slicerConfigRow slicerConfigRow2">
                            <div className="field">
                              <label className="label">Label</label>
                              <input className="input" placeholder="e.g. Ship Date" value={filter.label} onChange={(e) => updateFilter(filter.id, { label: e.target.value })} onBlur={() => requestSave()} />
                            </div>
                            {!isDR ? (
                              <div className="settingsCheckboxField">
                                <input type="checkbox" checked={filter.multi} onChange={(e) => { updateFilter(filter.id, { multi: e.target.checked }); requestSave(); }} />
                                <span style={{ fontSize: 13 }}>Multi</span>
                              </div>
                            ) : null}
                          </div>

                          {/* List-only: source + column */}
                          {!isDR ? (
                            <div className="slicerConfigRow slicerConfigRow3">
                              <div className="field">
                                <label className="label">Values from</label>
                                <select
                                  className="input"
                                  value={currentSelectValue}
                                  onChange={(e) => {
                                    const raw = e.target.value;
                                    if (!raw) {
                                      updateFilter(filter.id, { source_widget_id: "", source_viz: undefined, source_column: "", bindings: [] });
                                      requestSave();
                                      return;
                                    }
                                    const prevSourceWidgetId = filter.source_widget_id;
                                    if (raw.startsWith("widget:")) {
                                      const wid = raw.slice("widget:".length);
                                      const newCols = columnsForWidget(wid);
                                      const sourceColumn = newCols[0] ?? "";
                                      const nextBindings = filter.bindings.filter((b) => b.widget_id !== wid && b.widget_id !== prevSourceWidgetId);
                                      if (wid && sourceColumn) nextBindings.push({ widget_id: wid, kind: "column", column: sourceColumn });
                                      updateFilter(filter.id, { source_widget_id: wid, source_viz: undefined, source_column: sourceColumn, bindings: nextBindings });
                                      requestSave();
                                      return;
                                    }
                                    if (raw.startsWith("viz:")) {
                                      const key = raw.slice("viz:".length);
                                      let picked: PageWidgetSource | null = null;
                                      for (const row of bindableSources) {
                                        for (const t of row.targets) {
                                          const port = (t.port_id || "tabular_out").trim();
                                          const src: PageWidgetSource =
                                            port && port !== "tabular_out"
                                              ? { type: "operation_node", operation_id: row.operation_id, node_id: t.node_id, port_id: port }
                                              : { type: "operation_node", operation_id: row.operation_id, node_id: t.node_id };
                                          if (widgetSourceKey(src) === key) { picked = src; break; }
                                        }
                                        if (picked) break;
                                      }
                                      if (!picked) return;
                                      const newCols = columnsForSourceKey(key);
                                      const sourceColumn = newCols[0] ?? "";
                                      const nextBindings = filter.bindings.filter((b) => b.widget_id !== prevSourceWidgetId);
                                      updateFilter(filter.id, { source_widget_id: "", source_viz: picked, source_column: sourceColumn, bindings: nextBindings });
                                      requestSave();
                                    }
                                  }}
                                >
                                  <option value="">Select source...</option>
                                  {(() => {
                                    const pageOpIds = new Set(flattenContentWidgets(widgets).map((w) => w.source.operation_id));
                                    return bindableSources.filter((row) => pageOpIds.has(row.operation_id));
                                  })().map((row) => {
                                    if (row.targets.length === 0) return null;
                                    return (
                                      <optgroup key={row.operation_id} label={row.operation_name}>
                                        {row.targets.map((t) => {
                                          const port = (t.port_id || "tabular_out").trim();
                                          const src: PageWidgetSource =
                                            port && port !== "tabular_out"
                                              ? { type: "operation_node", operation_id: row.operation_id, node_id: t.node_id, port_id: port }
                                              : { type: "operation_node", operation_id: row.operation_id, node_id: t.node_id };
                                          const sKey = widgetSourceKey(src);
                                          const onPageWidget = flattenContentWidgets(widgets).find((w) => widgetSourceKey(w.source) === sKey);
                                          const optLabel = t.name || "(unnamed)";
                                          if (onPageWidget) {
                                            return <option key={`w:${onPageWidget.id}`} value={`widget:${onPageWidget.id}`}>{optLabel} — on page</option>;
                                          }
                                          return <option key={`v:${sKey}`} value={`viz:${sKey}`}>{optLabel}</option>;
                                        })}
                                      </optgroup>
                                    );
                                  })}
                                </select>
                              </div>
                              <div className="field">
                                <label className="label">Column</label>
                                <select
                                  className="input"
                                  value={filter.source_column}
                                  onChange={(e) => {
                                    const sourceColumn = e.target.value;
                                    const nextBindings = filter.source_viz
                                      ? filter.bindings
                                      : filter.bindings.filter((b) => b.widget_id !== filter.source_widget_id);
                                    if (!filter.source_viz && filter.source_widget_id && sourceColumn) {
                                      nextBindings.push({ widget_id: filter.source_widget_id, kind: "column", column: sourceColumn });
                                    }
                                    updateFilter(filter.id, { source_column: sourceColumn, bindings: nextBindings });
                                    requestSave();
                                  }}
                                >
                                  <option value="">Select column...</option>
                                  {srcCols.map((col) => <option key={col} value={col}>{col}</option>)}
                                </select>
                              </div>
                            </div>
                          ) : null}

                          {/* Date range only: default dates */}
                          {isDR ? (
                            <div className="slicerConfigRow slicerConfigRow2">
                              <div className="field">
                                <label className="label">Default start</label>
                                <input
                                  className="input"
                                  type="date"
                                  value={filter.default_start ?? ""}
                                  onChange={(e) => updateFilter(filter.id, { default_start: e.target.value || undefined })}
                                  onBlur={() => requestSave()}
                                />
                              </div>
                              <div className="field">
                                <label className="label">Default end</label>
                                <input
                                  className="input"
                                  type="date"
                                  value={filter.default_end ?? ""}
                                  onChange={(e) => updateFilter(filter.id, { default_end: e.target.value || undefined })}
                                  onBlur={() => requestSave()}
                                />
                              </div>
                            </div>
                          ) : null}

                          {/* Applies to — one row per filterable widget. The row's right-hand
                              picker is a column dropdown for tantable/echarts/kpi widgets and a
                              parameter dropdown for reactive_visualization widgets. The runtime
                              dispatches client-filter vs server-execute by inspecting the binding
                              kind. AI-only widgets aren't filterable and are hidden. */}
                          <div style={{ marginTop: 8 }}>
                            <label className="label">Applies to</label>
                            <div className="slicerBindingsTable">
                              {flattenContentWidgets(widgets).map((w) => {
                                if (w.id === filter.source_widget_id) return null;
                                const labelKey = vizNodeLabelKey(w.source);
                                const wName = w.title_override || vizSourceNameByKey[labelKey] || w.id;
                                const port = resolvedSourcePort(w.source, resolvedItems[widgetSourceKey(w.source)]);
                                const target = bindableSources
                                  .find((r) => r.operation_id === w.source.operation_id)
                                  ?.targets.find((t) => t.node_id === w.source.node_id && t.port_id === port);
                                if (target?.node_type === "ai_provider") return null;
                                const binding = filter.bindings.find((b) => b.widget_id === w.id);

                                if (target?.node_type === "reactive_visualization") {
                                  const params = Array.isArray(target.parameters) ? target.parameters : [];
                                  const boundParam = binding?.kind === "param" ? binding.param_name : "";
                                  const selectedParam = params.find((p) => p.name === boundParam);
                                  const paramMissing = !!boundParam && !selectedParam;
                                  let typeWarn: string | null = null;
                                  if (selectedParam) {
                                    if (filter.multi && (selectedParam.type === "text" || selectedParam.type === "number" || selectedParam.type === "identifier")) {
                                      typeWarn = `multi-select incompatible with ${selectedParam.type}`;
                                    } else if (!filter.multi && selectedParam.type === "text_array") {
                                      typeWarn = "single-select incompatible with text_array";
                                    }
                                  }
                                  return (
                                    <div key={w.id} className="slicerBindingRow">
                                      <span className="slicerBindingName">{wName}</span>
                                      <select
                                        className="input slicerBindingSelect"
                                        value={boundParam}
                                        onChange={(e) => {
                                          const pn = e.target.value;
                                          const newBindings = filter.bindings.filter((b) => b.widget_id !== w.id);
                                          if (pn) newBindings.push({ widget_id: w.id, kind: "param", param_name: pn });
                                          updateFilter(filter.id, { bindings: newBindings });
                                          requestSave();
                                        }}
                                      >
                                        <option value="">Not filtered</option>
                                        {params.map((p) => (
                                          <option key={p.name} value={p.name}>${p.name} ({p.type})</option>
                                        ))}
                                      </select>
                                      {paramMissing ? (
                                        <span className="pill pillWarn" style={{ marginLeft: 6 }}>
                                          Parameter <code>${boundParam}</code> no longer declared
                                        </span>
                                      ) : null}
                                      {typeWarn ? (
                                        <span className="pill pillWarn" style={{ marginLeft: 6 }}>{typeWarn}</span>
                                      ) : null}
                                    </div>
                                  );
                                }

                                const wCols = columnsForWidget(w.id);
                                const boundColumn = binding?.kind === "column" ? binding.column : "";
                                return (
                                  <div key={w.id} className="slicerBindingRow">
                                    <span className="slicerBindingName">{wName}</span>
                                    <select
                                      className="input slicerBindingSelect"
                                      value={boundColumn}
                                      onChange={(e) => {
                                        const col = e.target.value;
                                        const newBindings = filter.bindings.filter((b) => b.widget_id !== w.id);
                                        if (col) newBindings.push({ widget_id: w.id, kind: "column", column: col });
                                        updateFilter(filter.id, { bindings: newBindings });
                                        requestSave();
                                      }}
                                    >
                                      <option value="">Not filtered</option>
                                      {wCols.map((col) => <option key={col} value={col}>{col}</option>)}
                                    </select>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : null}

              {/* ── PAGE TAB: AI ── */}
              {inspectorTab === "ai" ? (
                <div className="inspectorTabPanel">
                  <div className="aiAssistPanel">
                    <div className="aiAssistPanelHeader">Page AI — coming soon</div>
                    <div className="muted" style={{ fontSize: 13, lineHeight: 1.5 }}>
                      The page AI will help you add widgets, configure filter interactions, adjust layout, and describe the page's purpose in plain language.
                    </div>
                  </div>
                </div>
              ) : null}

            </>
          )}
        </div>
        </InspectorFlyout>

      </div>

      {modalWidget ? (
        <div className="modalOverlay" role="presentation" onClick={cancelPendingWidget}>
          <div className="modal widgetSettingsModal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="widgetSettingsHeader">
              <h2 className="widgetSettingsTitle">Add Widget</h2>
              <button className="widgetSettingsClose" type="button" onClick={cancelPendingWidget} aria-label="Close">&times;</button>
            </div>

            <div className="widgetSettingsBody">
              <div className="field">
                <label className="label">Data source</label>
                <div className="muted" style={{ fontSize: 13, lineHeight: 1.5 }}>
                  {vizSourceNameByKey[vizNodeLabelKey(modalWidget.source)] ?? modalWidget.source.operation_id}
                  <br />
                  <span style={{ opacity: 0.85 }}>
                    {(() => {
                      const p = resolvedSourcePort(modalWidget.source, resolvedItems[widgetSourceKey(modalWidget.source)]);
                      if (p === "tabular_out") return "Port: table / chart";
                      if (p === "content_out") return "Port: AI content";
                      if (p === "refs_out") return "Port: citations";
                      return `Port: ${p}`;
                    })()}
                  </span>
                </div>
                {bindableSources.find((s) => s.operation_id === modalWidget.source.operation_id)?.operation_status === "validate" ? (
                  <div className="muted" style={{ fontSize: 12, marginTop: 8, color: "var(--warning,#d97706)" }}>
                    This operation requires validation before data can be refreshed.
                  </div>
                ) : null}
              </div>

              <div className="field">
                <label className="label">Title override</label>
                <input
                  className="input"
                  placeholder="Leave blank to use the default title"
                  value={modalWidget.title_override ?? ""}
                  onChange={(e) => updatePendingTitleOverride(e.target.value || undefined)}
                />
              </div>

              <div className="field">
                <label className="label">Size</label>
                <div className="widgetSizePresets">
                  {([{ label: "Full", w: 12 }, { label: "Half", w: 6 }, { label: "Third", w: 4 }, { label: "Quarter", w: 3 }] as const).map((preset) => (
                    <button
                      key={preset.label}
                      className={`widgetSizePresetBtn${modalWidget.grid.w === preset.w ? " active" : ""}`}
                      type="button"
                      onClick={() => updatePendingGrid({ x: 0, w: preset.w })}
                    >{preset.label}</button>
                  ))}
                </div>
              </div>

              <div className="field">
                <label className="label">Position &amp; dimensions</label>
                <div className="widgetGridInputs">
                  {(["x", "y", "w", "h"] as const).map((key) => (
                    <div key={key} className="widgetGridField">
                      <span className="widgetGridLabel">{key.toUpperCase()}</span>
                      <input
                        className="input widgetGridInput"
                        type="number"
                        value={modalWidget.grid[key]}
                        onChange={(e) => updatePendingGrid({ [key]: Number(e.target.value) })}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="widgetSettingsFooter">
              <div className="widgetSettingsFooterActions">
                <button className="btn" type="button" onClick={cancelPendingWidget}>Cancel</button>
                <button className="btn btnPrimary" type="button" onClick={commitPendingWidget}>OK</button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <PageSourcePickerModal
        open={sourcePickerOpen}
        title={sourcePickerChangeWidgetId ? "Change data source" : "Choose data source"}
        sources={bindableSources}
        initialSource={sourcePickerInitial}
        onConfirm={handleSourcePicked}
        onClose={closeSourcePicker}
      />
    </PageLayout>
  );
}
